import { h, render, Component, createRef } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { IntlProvider, Text, Localizer, withText } from 'preact-i18n';
import { route } from 'preact-router';
import Markdown from 'preact-markdown';
import linkstate from 'linkstate';
import md5 from 'crypto-js/md5';

import localUtil from '../../lib/util';
import fields from '../../lib/fields';
import { printViaIframe, printLabelViaIframe } from '../../lib/printHelper';
import { cartAddUrl } from '../../lib/cartAddDecision';

import ViewFields from '../../components/form/viewFields';
import DyrejournalSaleSearch from '../../components/dyrejournal/search';
import Barcode from '../../components/gui/barcode';
import QrCode from '../../components/gui/qrcode';
import QrCodeDebug from '../../components/gui/qrcode-debug';

import Input from '../../components/form/input';
import ScrollIntoView from '../../components/gui/scrollIntoView';
import FitTextSvg from '../gui/fitTextSvg';
import Pagination from '../../components/gui/pagination';
import MiniGraph from '../../components/lineGraph/mini';
import DealersSection from '../../components/dyrejournal/dealersSection';
import CsvPriceUpload from '../../components/product/csvPriceUpload';
import ProductLibraryImport from '../../components/product/productLibraryImport';
import Tooltip from '../../components/gui/tooltip';
import { getAccountName, getAccountOptions } from '../../lib/accountMap';
import { formatPriceCell } from '../../lib/priceFormat';

import definitionNo from '../../languages/no.json';
import definitionEn from '../../languages/en.json';
import { appConfirm } from '../../lib/dialogs';
import { beginDialogAction } from '../../lib/dialogAction';

const countryMap = {
    no: definitionNo,
    en: definitionEn,
    default: definitionEn,
};

// Column definitions for the product table
const COLUMN_DEFINITIONS = [
    { key: 'lineNumber', label: 'Line #', defaultVisible: true },
    { key: 'velocity', label: 'Movement', defaultVisible: false },
    { key: 'productNumber', label: 'Product Number', defaultVisible: true },
    { key: 'webshop', label: 'Webshop', defaultVisible: true },
    { key: 'isMemberProduct', label: 'Member Product', defaultVisible: false },
    { key: 'idealStock', label: 'Ideal Stock', defaultVisible: true },
    { key: 'inStock', label: 'In Stock', defaultVisible: true },
    { key: 'addStock', label: 'Add Stock', defaultVisible: true },
    { key: 'name', label: 'Name', defaultVisible: true },
    { key: 'category', label: 'Category', defaultVisible: false },
    { key: 'journal', label: 'Journal Macro', defaultVisible: false },
    { key: 'drug', label: 'Drug Connected', defaultVisible: false },
    { key: 'account', label: 'Account', defaultVisible: true },
    { key: 'vat', label: 'VAT', defaultVisible: true },
    { key: 'price', label: 'Price', defaultVisible: true },
    { key: 'medicine', label: 'Medicine', defaultVisible: true },
    { key: 'fee', label: 'Fee', defaultVisible: true },
    { key: 'equipment', label: 'Equipment', defaultVisible: true },
    { key: 'other', label: 'Other', defaultVisible: true },
    { key: 'discountAmount', label: 'Discount Amount', defaultVisible: true },
    { key: 'saleType', label: 'Sale Type', defaultVisible: true },
    { key: 'outPrice', label: 'Out Price', defaultVisible: true },
    { key: 'reminderDays', label: 'Reminder Days', defaultVisible: false },
    { key: 'reminderSmsText', label: 'Reminder SMS Text', defaultVisible: false },
    { key: 'tripletex', label: 'Tripletex', defaultVisible: true },
    { key: 'delete', label: 'Delete', defaultVisible: true },
];

@withText(props => ({
    productTitle: <Text id='dyrejournal.product.search-title'>Search for product</Text>,
    searchTitle: <Text id='dyrejournal.product-title'>Search for products</Text>,
    adjustPricesTitle: <Text id='dyrejournal.product-priceadjust-title'>Adjust all prices in %</Text>,
    productGroupTitle: <Text id='dyrejournal.product-account-title'>Enter account number</Text>,
    productCategoryTitle: <Text id='dyrejournal.product-category-title'>Enter category</Text>,
}))
@observer
class AllProducts extends Component {
	constructor(props) {
        super(props);
        // Initialize visible columns from defaults (1 = visible, 0 = hidden)
        const defaultVisibleColumns = {};
        COLUMN_DEFINITIONS.forEach(col => {
            defaultVisibleColumns[col.key] = col.defaultVisible ? 1 : 0;
        });

        this.state = {
            sections: fields.get('journal'),
            sort: 'name',
            selectedCell: { col: 0, row: 0 },  // Starting at the top-left cell (row 0, column 0)
            editingCell: { col: 0, row: 0 },
            visibleColumns: defaultVisibleColumns,
            showColumnConfig: false,
            hoverImageProduct: null,
            hoverImagePos: { left: 0, top: 0 },
            showProductStats: false,
            showAccountingPlan: false,
            showDealers: false,
            filterDealer: '',
            // Tripletex matching state
            isBulkMatchingTripletex: false,
            bulkTripletexMatches: {},
            tripletexMatchError: null,
            // Breece Cloud sync state
            isSyncingBreece: false,
            breeceSyncResult: null,
            breeceSyncError: null,
            // CSV Price Upload state
            showCsvPriceUpload: false,
            showProductLibrary: false,
            // Tripletex expanded row state
            expandedTripletexProductId: null,
            // Price adjustment wizard state
            showPriceAdjustmentWizard: false,
            wizardStep: 1,
            wizardMode: 'percent',
            wizardPercent: '',
            wizardAmount: '',
            wizardMargin: '',
            wizardRounding: 0,
            wizardRoundIncVat: true,
            wizardComponents: { price: true, medicine: true, fee: true, equipment: true, other: true },
            wizardAccount: '',
            wizardCategory: '',
            wizardSearch: '',
            wizardPreview: null,
            wizardPreviewMessage: null,
            wizardPreviewError: null,
            wizardIsLoading: false,
            wizardApplyDone: false,
        };
        this.searchTimer = null;
        this.tbodyRef = null;

        this.functions = {
            // 'checkForJournal': this.checkForJournal,
            // 'addDiagnose': this.onClickAddDiagnose,
            // 'addMedicine': this.onClickAddMedicine,
        };
    }

    setFocusToTable = () => {
        if (this.tbodyRef) {
            this.tbodyRef.focus();
        }
    }

    loadColumnPreferences = () => {
        const { userStore } = this.props.stores;
        const { user = {} } = userStore;
        const { settings = {} } = user;

        const visibleColumns = {};
        COLUMN_DEFINITIONS.forEach(col => {
            const settingKey = `productTableColumn_${col.key}`;
            if (settings[settingKey] !== undefined) {
                visibleColumns[col.key] = settings[settingKey];
            } else {
                visibleColumns[col.key] = col.defaultVisible ? 1 : 0;
            }
        });
        this.setState({ visibleColumns });
    }

    saveColumnPreference = async (columnKey, visible) => {
        const { userStore } = this.props.stores;
        const { user = {} } = userStore;
        if (user.id) {
            await userStore.saveField(user.id, `settings.productTableColumn_${columnKey}`, visible ? 1 : 0, true);
        }
    }

    toggleColumnConfig = () => {
        this.setState({ showColumnConfig: !this.state.showColumnConfig });
    }

    onToggleProductStats = async () => {
        const { showProductStats } = this.state;
        if (!showProductStats) {
            const { productStore } = this.props.stores;
            await productStore.loadProductStats();
        }
        this.setState({ showProductStats: !showProductStats });
    }

    onToggleAccountingPlan = async () => {
        const { showAccountingPlan } = this.state;
        if (!showAccountingPlan) {
            const { productStore } = this.props.stores;
            const { search, productCategory, productGroup, filterDealer } = this.state;
            await productStore.loadAccountingPlan({
                search,
                category: productCategory,
                account: productGroup, // productGroup holds the account-number filter
                dealer: filterDealer,
            });
        }
        this.setState({ showAccountingPlan: !showAccountingPlan });
    }

    onToggleDealers = async () => {
        const { showDealers } = this.state;
        if (!showDealers) {
            const { dealerStore } = this.props.stores;
            await dealerStore.loadPrefixes();
        }
        this.setState({ showDealers: !showDealers });
    }

    onClickBulkMatchTripletex = async () => {
        const { tripletexStore, productStore, customerStore } = this.props.stores;
        const { products } = productStore;
        const { customer } = customerStore;

        this.setState({
            isBulkMatchingTripletex: true,
            bulkTripletexMatches: {},
            tripletexMatchError: null,
        });

        try {
            // Fetch all Tripletex products for this customer
            const response = await tripletexStore.loadAllTripletexProducts();

            if (response?.status === 200) {
                // Check for Tripletex API error in the inner response
                const tripletexStatus = response.data?.status;
                if (tripletexStatus && tripletexStatus !== 200) {
                    // Tripletex API returned an error
                    const errorMessage = response.data?.message || 'Tripletex API error';
                    this.setState({
                        isBulkMatchingTripletex: false,
                        tripletexMatchError: `Tripletex: ${errorMessage}`,
                    });
                    return;
                }

                const tripletexProducts = response.data?.values || [];
                // Match locally
                const bulkMatches = productStore.matchProductsWithTripletexProducts(
                    products,
                    tripletexProducts,
                    customer?.id
                );

                this.setState({
                    isBulkMatchingTripletex: false,
                    bulkTripletexMatches: bulkMatches,
                    tripletexMatchError: null,
                });
            } else {
                this.setState({
                    isBulkMatchingTripletex: false,
                    tripletexMatchError: response?.message || 'Failed to load Tripletex products',
                });
            }
        } catch (error) {
            console.error('Tripletex bulk match error:', error);
            this.setState({
                isBulkMatchingTripletex: false,
                tripletexMatchError: error.message || 'An error occurred',
            });
        }
    }

    clearBulkTripletexMatches = () => {
        this.setState({ bulkTripletexMatches: {}, tripletexMatchError: null, expandedTripletexProductId: null });
    }

    onClickShowTripletexMatch = (e, productId) => {
        e.stopPropagation();
        const { expandedTripletexProductId } = this.state;
        this.setState({
            expandedTripletexProductId: expandedTripletexProductId === productId ? null : productId,
        });
    }

    onClickCloseTripletexMatch = (e) => {
        e.stopPropagation();
        this.setState({ expandedTripletexProductId: null });
    }

    onClickLinkTripletex = async (productId, tripletexProductId) => {
        const { productStore } = this.props.stores;
        await productStore.saveTripletexProductId(productId, tripletexProductId);
        // Update local match state
        const { bulkTripletexMatches } = this.state;
        if (bulkTripletexMatches[productId]) {
            this.setState({
                bulkTripletexMatches: {
                    ...bulkTripletexMatches,
                    [productId]: {
                        ...bulkTripletexMatches[productId],
                        linked: true,
                    },
                },
            });
        }
    }

    onClickUnlinkTripletex = async (productId) => {
        const { productStore } = this.props.stores;
        await productStore.saveTripletexProductId(productId, 0);
    }

    onClickSyncToBreece = async () => {
        const { productStore } = this.props.stores;
        const { products } = productStore;
        const { search, productCategory, productGroup, filterDealer } = this.state;

        this.setState({
            isSyncingBreece: true,
            breeceSyncResult: null,
            breeceSyncError: null,
        });

        try {
            // Get the current filtered product IDs
            const productIds = products.map(p => p.id);

            // Sync to Breece Cloud via API
            const response = await util.fetchApi('/api/integrations/breece/products/sync', { method: 'POST' }, {
                productIds,
                search: search || undefined,
                category: productCategory || undefined,
                account: productGroup || undefined,
            });

            if (response?.status === 200) {
                this.setState({
                    isSyncingBreece: false,
                    breeceSyncResult: response.data,
                    breeceSyncError: null,
                });
            } else {
                this.setState({
                    isSyncingBreece: false,
                    breeceSyncResult: null,
                    breeceSyncError: response?.message || 'Failed to sync products to Breece Cloud',
                });
            }
        } catch (error) {
            console.error('Breece Cloud sync error:', error);
            this.setState({
                isSyncingBreece: false,
                breeceSyncResult: null,
                breeceSyncError: error.message || 'An error occurred',
            });
        }
    }

    clearBreeceSyncResult = () => {
        this.setState({ breeceSyncResult: null, breeceSyncError: null });
    }

    toggleCsvPriceUpload = () => {
        this.setState(prevState => ({
            showCsvPriceUpload: !prevState.showCsvPriceUpload,
        }));
    }

    toggleProductLibrary = () => {
        this.setState(prevState => ({ showProductLibrary: !prevState.showProductLibrary }));
    }

    onCsvPriceUploadComplete = () => {
        this.loadAll();
    }

    toggleColumn = async (columnKey) => {
        const { visibleColumns } = this.state;
        const newVisible = visibleColumns[columnKey] === 1 ? 0 : 1;
        this.setState({
            visibleColumns: {
                ...visibleColumns,
                [columnKey]: newVisible,
            },
        });
        this.saveColumnPreference(columnKey, newVisible === 1);

        // Load weekly stats when velocity column is enabled
        if (columnKey === 'velocity' && newVisible === 1) {
            await this.loadWeeklyStatsForCurrentProducts();
        }
    }

    loadWeeklyStatsForCurrentProducts = async () => {
        const { productStore } = this.props.stores;
        const { products } = productStore;
        if (products && products.length > 0) {
            const productIds = products.map(p => p.id);
            await productStore.loadTableWeeklyStats(productIds);
        }
    }

    isColumnVisible = (columnKey) => {
        const { visibleColumns } = this.state;
        return visibleColumns[columnKey] !== 0;
    }

    loadAll = async (props = this.props, emptyFirst = true, startAtTheBeginning = false) => {
        const { productStore, userStore, appState } = this.props.stores;
        const { limit, currentPage } = productStore;
        const { productGroup, productCategory, sort, lessThanIdealStock, filterDealer, filterHasJournal, filterHasDrug } = this.state;
        const { showFrontpageShop } = this.props;
        const { currentCustomer = {} } = appState;

        const query = {
            category: productCategory,
            account: productGroup,
            lessThanIdealStock,
            dealer: filterDealer || undefined,
            hasJournal: filterHasJournal ? 1 : undefined,
            hasDrug: filterHasDrug ? 1 : undefined,
        };
        // if (showFrontpageShop) {
        //     query.customer = currentCustomer?.id;
        // }

        await productStore.load({
            query,
            sort,
            addData: ['veterinaries', 'dealers', 'allProducts', 'latestUpdateProducts', 'likes', 'comments', 'tags'],
            limit,
            offset: (currentPage - 1) * limit,
        });

        // Load weekly stats if velocity column is visible
        if (this.isColumnVisible('velocity')) {
            await this.loadWeeklyStatsForCurrentProducts();
        }

        this.setFocusToTable();
    }

    setPage = (page) => {
        const { productStore } = this.props.stores;
        productStore.updateKeyValue('currentPage', page);
        this.loadAll();
    }

    onChooseLimit = async e => {
        const { value } = e.target;
        const limit = parseInt(value, 10);
        const { productStore } = this.props.stores;
        productStore.updateKeyValue('limit', limit);
        productStore.updateKeyValue('currentPage', 1); // Reset to first page when limit changes
        this.loadAll();
    }

    onClickChooseProduct = e => {
        e.preventDefault();
        e.stopPropagation();
        const { onClickChooseProduct, saveSale = () => {} } = this.props;
        if (onClickChooseProduct) {
            return onClickChooseProduct(e);
        }
        const { id } = e.target.closest('tr').dataset;
        const { drawerLevel = 1 } = this.props;
		const { appState, saleStore, productStore, userStore } = this.props.stores;
        const { user = {} } = userStore;

        const { newSale } = saleStore;
        const { products = [] } = newSale;
        const { products: allProducts } = productStore;
        const selectedItem = allProducts.find(p => p.id === parseInt(id, 10));
        if (selectedItem) {
            const addProducts = [{
                ...selectedItem,
                qty: 1,
                createdDate: new Date(),
                md5: md5(JSON.stringify({ ...localUtil.safeProductForMd5(selectedItem), createdDate: new Date() })).toString(),
                saleUser: user.id,
                saleDepartment: user.currentCustomerDepartment,
            }];
            const addSubProducts = localUtil.extractSubProductsWithLevel2(
                selectedItem,
                allProducts,
                { user, animal: null }
            );
            if (addSubProducts?.length > 0) {
                // Normalize essential fields to ensure calculations work correctly
                addSubProducts.forEach(p => {
                    p.qty = p.qty || 1;
                    p.price = p.price ?? 0;
                    p.vat = p.vat ?? 0;
                    p.discount = p.discount ?? 0;
                    p.discountAmount = p.discountAmount ?? 0;
                });
                addProducts.push(...addSubProducts);
            }
            saleStore.updateObjectKeyValue('newSale', 'products', [...products, ...addProducts]);
            saleStore.updateObjectKeyValue('newSale', 'productAdded', true);
            saveSale({ calledFrom: 'searchProductCallback.productNoJournal.action' });
            appState.toggleDrawerRight(false, drawerLevel);
        }
    }

    onClickEditProduct = e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('tr').dataset;
        const { drawerLevel = 1 } = this.props;
		const { appState, saleStore } = this.props.stores;
		const { drawerHeightMediumLarge } = appState;
		appState.openDrawer('editProduct', {
			height: drawerHeightMediumLarge,
            id,
            callback: () => {
                // appState.toggleDrawer(false, drawerLevel + 1);
                // if (product) {
                    // const { newSale, productResults } = saleStore;
                    // const { products } = newSale;
                    // saleStore.updateObjectKeyValue('newSale', 'products', [...products, { ...product, qty: 1 }]);
                    // saleStore.updateObjectKeyValue('newSale', 'productAdded', true);
                    // setTimeout(() => {
                        // this.loadAll(this.props, false);
                    // }, 1000);
                // }
                this.setFocusToTable();
            },
		}, drawerLevel + 2);
    }

    onClickDeleteProduct = async e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('tr').dataset;
        const dialogAction = beginDialogAction(this, `onClickDeleteProduct:${parseInt(id, 10)}`);
        if (!dialogAction) return false;
        try {
            if (!((await appConfirm('Are you sure you want to delete this product?')) && dialogAction.isCurrent())) {
                return;
            }

            const { productStore } = this.props.stores;
            console.log('onClickDeleteProduct', id, parseInt(id, 10));
            await productStore.delete(parseInt(id, 10));
            setTimeout(() => {
                // this.loadAll(this.props, false);
            }, 1000);
        } finally {
            dialogAction.finish();
        }
    }

    onClickNewProduct = e => {
        console.log('onClickNewProduct', e);
        const { drawerLevel = 1, saveSale = () => {} } = this.props;
		const { appState, saleStore, productStore } = this.props.stores;
		const { drawerHeightMediumLarge } = appState;

		appState.openDrawer('newProduct', {
			height: drawerHeightMediumLarge,
            isNew: true,
            hideAdmin: true,
            callback: async product => {
                appState.toggleDrawer(false, drawerLevel + 2);
                if (product) {
                    const { newSale, productResults } = saleStore;
                    const { products } = newSale;
                    const productWithDate = { ...product, qty: 1, createdDate: new Date() };
                    saleStore.updateObjectKeyValue('newSale', 'products', [...products, {
                        ...productWithDate,
                        md5: md5(JSON.stringify(productWithDate)).toString(),
                    }]);
                    saleStore.updateObjectKeyValue('newSale', 'productAdded', true);
                    console.log('allProducts.onClickNewProduct: about to save sale', {
                        hasId: !!saleStore.newSale.id,
                        productsCount: saleStore.newSale.products?.length,
                        visitorsCount: saleStore.newSale.visitors?.length,
                        productAdded: saleStore.newSale.productAdded,
                    });
                    const saveResult = await saveSale({ calledFrom: 'allProducts.onClickNewProduct' });
                    console.log('allProducts.onClickNewProduct: save result', saveResult, 'newSale.id:', saleStore.newSale.id);
                    setTimeout(async () => {
                        this.loadAll(this.props, false, true);
                        // await productStore.load();
                    }, 1000);
                }
                this.setFocusToTable();
            },
		}, drawerLevel + 2);
    }

    // searchProductCallback = selectedItem => {
    //     const { appState, saleStore, productStore } = this.props.stores;
    //     const { drawerLevel = 1 } = this.props;
    //     const { newSale } = saleStore;
    //     const { products = [] } = newSale;
    //     saleStore.updateObjectKeyValue('newSale', 'products', [...products, selectedItem]);
    //     appState.toggleDrawerRight(false, drawerLevel);
    // }


    viewTags = obj => {
        if (obj.tags?.length === 0 && obj.loans?.length === 0 && !obj.ownedByOther && !obj.url && !obj.parents?.length > 0 && !obj.files?.length > 0) {
            return '';
        }
        const { userStore } = this.props.stores;
        const { user = {} } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');
        const tagClass = darkmode ? 'dark' : 'light';

        return (<>
            <div class='w-100 overflow-hidden'>
                <div class='box-line-tags d-flex flex-nowrap'>
                    {obj.files?.length > 0 && <>
                        <span class={`badge text-bg-light me-1 mt-1`}>
                            <i class='fa-duotone fa-file' /> <span class='font-weight-lighter'> x {obj.files.length}</span>
                        </span>
                    </>}
                    {obj.tags?.length > 0 && obj.tags.map(tag => {
                        return (<>
                            <span
                                class={`badge text-bg-${tagClass} me-1 mt-1`}
                                onClick={this.toggleTagFilter}
                                data-tag={tag.name}
                            >#{tag.name}</span>
                        </>);
                    })}
                </div>
            </div>
        </>);
    }

    searchInput = (e) => {
        const search = e.target.value;
        this.setState({ search });

        clearTimeout(this.searchTimer);
        if (search.length >= 3) {
            this.searchTimer = setTimeout(async () => {
                this.doSearch(search);
            }, 300);
        }
    }

    togglePriceAdjustmentWizard = () => {
        this.setState(prevState => {
            if (prevState.showPriceAdjustmentWizard) {
                // Close and reset
                return {
                    showPriceAdjustmentWizard: false,
                    wizardStep: 1,
                    wizardMode: 'percent',
                    wizardPercent: '',
                    wizardAmount: '',
                    wizardMargin: '',
                    wizardRounding: 0,
                    wizardRoundIncVat: true,
                    wizardComponents: { price: true, medicine: true, fee: true, equipment: true, other: true },
                    wizardAccount: '',
                    wizardCategory: '',
                    wizardSearch: '',
                    wizardPreview: null,
                    wizardPreviewMessage: null,
                    wizardPreviewError: null,
                    wizardIsLoading: false,
                    wizardApplyDone: false,
                };
            }
            return { showPriceAdjustmentWizard: true, wizardStep: 1 };
        });
    }

    wizardGoToStep = (step) => {
        const { wizardMode, wizardPercent, wizardAmount, wizardMargin } = this.state;

        // Validate when leaving step 1
        if (step > 1 && this.state.wizardStep === 1) {
            let valueEntered = false;
            if (wizardMode === 'percent' && wizardPercent !== '') valueEntered = true;
            if (wizardMode === 'fixed' && wizardAmount !== '') valueEntered = true;
            if (wizardMode === 'margin' && wizardMargin !== '') valueEntered = true;
            if (!valueEntered) return;

            if (wizardMode === 'percent') {
                const percent = parseFloat(wizardPercent);
                if (percent < -100) {
                    this.setState({
                        wizardPreviewError: 'Prosentjustering kan ikke være mindre enn -100%. En justering på -100% vil sette prisene til 0.',
                    });
                    return;
                }
            }
        }

        this.setState({ wizardStep: step, wizardPreviewError: null }, () => {
            if (step === 3) {
                this.wizardRunPreview();
            }
        });
    }

    wizardRunPreview = async () => {
        const {
            wizardMode, wizardPercent, wizardAmount, wizardMargin,
            wizardRounding, wizardRoundIncVat, wizardComponents, wizardAccount, wizardCategory, wizardSearch,
        } = this.state;
        const { productStore } = this.props.stores;

        const components = Object.keys(wizardComponents).filter(k => wizardComponents[k]);

        this.setState({ wizardIsLoading: true, wizardPreviewError: null, wizardPreview: null, wizardPreviewMessage: null });
        const response = await productStore.updateAllPrices({
            account: wizardAccount || undefined,
            category: wizardCategory || undefined,
            search: wizardSearch || undefined,
            dryRun: true,
            mode: wizardMode,
            percent: wizardMode === 'percent' ? wizardPercent : undefined,
            amount: wizardMode === 'fixed' ? parseFloat(wizardAmount) : undefined,
            margin: wizardMode === 'margin' ? parseFloat(wizardMargin) : undefined,
            components,
            rounding: wizardRounding || undefined,
            roundIncVat: wizardRounding > 0 ? wizardRoundIncVat : undefined,
        });
        if (response?.status === 200) {
            this.setState({ wizardPreview: response.data, wizardPreviewMessage: response.message, wizardIsLoading: false });
        } else if (response?.message) {
            this.setState({ wizardPreviewError: response.message, wizardIsLoading: false });
        } else {
            this.setState({ wizardPreviewError: 'Noe gikk galt. Prøv igjen.', wizardIsLoading: false });
        }
    }

    wizardApply = async () => {
        const dialogAction = beginDialogAction(this, 'wizardApply', () => [
            this.state.wizardComponents,
        ]);
        if (!dialogAction) return false;
        try {
            const {
                wizardMode, wizardPercent, wizardAmount, wizardMargin,
                wizardRounding, wizardRoundIncVat, wizardComponents, wizardAccount, wizardCategory, wizardSearch, wizardPreview,
            } = this.state;
            const { productStore } = this.props.stores;

            const count = wizardPreview?.filter(p => !p.skipped).length || 'alle';
            let confirmMsg = '';
            if (wizardMode === 'percent') {
                confirmMsg = `Er du sikker på at du vil justere ${count} produkter med ${wizardPercent}%?`;
            } else if (wizardMode === 'fixed') {
                confirmMsg = `Er du sikker på at du vil justere ${count} produkter med ${parseFloat(wizardAmount) >= 0 ? '+' : ''}${wizardAmount} kr?`;
            } else if (wizardMode === 'margin') {
                confirmMsg = `Er du sikker på at du vil sette pris til ${wizardMargin}% margin over innkjøpspris for ${count} produkter?`;
            }
            if (!((await appConfirm(confirmMsg)) && dialogAction.isCurrent())) {
                return;
            }

            const components = Object.keys(wizardComponents).filter(k => wizardComponents[k]);

            this.setState({ wizardIsLoading: true });
            const response = await productStore.updateAllPrices({
                account: wizardAccount || undefined,
                category: wizardCategory || undefined,
                search: wizardSearch || undefined,
                mode: wizardMode,
                percent: wizardMode === 'percent' ? wizardPercent : undefined,
                amount: wizardMode === 'fixed' ? parseFloat(wizardAmount) : undefined,
                margin: wizardMode === 'margin' ? parseFloat(wizardMargin) : undefined,
                components,
                rounding: wizardRounding || undefined,
                roundIncVat: wizardRounding > 0 ? wizardRoundIncVat : undefined,
            });
            this.setState({ wizardIsLoading: false });
            if (response?.status === 201) {
                this.setState({ wizardApplyDone: true });
                setTimeout(() => {
                    this.loadAll(this.props, false);
                }, 1000);
            }
        } finally {
            dialogAction.finish();
        }
    }

    wizardModeChange = (e) => {
        const wizardMode = e.target.value;
        this.setState({
            wizardMode,
            wizardPercent: '',
            wizardAmount: '',
            wizardMargin: '',
            wizardPreview: null,
            wizardPreviewMessage: null,
            wizardPreviewError: null,
        });
    }

    wizardComponentToggle = (component) => {
        this.setState(prevState => ({
            wizardComponents: {
                ...prevState.wizardComponents,
                [component]: !prevState.wizardComponents[component],
            },
        }));
    }

    productGroupInput = async (e) => {
        const { productCategory, sort, filterDealer } = this.state;
        const { productStore } = this.props.stores;
        const productGroup = e.target.value;
        this.setState({ productGroup });
        await productStore.load({ query: { category: productCategory, account: productGroup, dealer: filterDealer || undefined }, sort });
    }

    productCategoryInput = async (e) => {
        const { productGroup, sort, filterDealer } = this.state;
        const { productStore } = this.props.stores;
        const productCategory = e.target.value;
        this.setState({ productCategory });
        await productStore.load({ query: { category: productCategory, account: productGroup, dealer: filterDealer || undefined }, sort });
    }

    dealerFilterInput = async (e) => {
        const { productGroup, productCategory, sort } = this.state;
        const { productStore } = this.props.stores;
        const filterDealer = e.target.value;
        this.setState({ filterDealer });
        await productStore.load({ query: { category: productCategory, account: productGroup, dealer: filterDealer || undefined }, sort });
    }


    async doSearch(search) {
        const { productCategory, productGroup, sort, filterDealer } = this.state;
        const { productStore } = this.props.stores;
        productStore.updateKeyValue('offset', 0);
        if (search) {
            // console.log('doUpdateField.saveField', id, field, value);
            const result = await productStore.load({ query: { search, category: productCategory, account: productGroup, dealer: filterDealer || undefined }, limit: 500, offset: 0, skipUpdate: false, sort  });
        } else {
            const result = await productStore.load({ query: { category: productCategory, account: productGroup, dealer: filterDealer || undefined }, limit: 500, offset: 0, skipUpdate: false, sort  });
        }
    }

    clearSearch = () => {
        this.setState({ search: '' });
        this.doSearch('');
    }

    handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            this.doSearch(this.state.search);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            this.clearSearch();
        }
    }

    onClickToggleMultiple = () => {
        const { showEditMultiple } = this.state;
        this.setState({ showEditMultiple: !showEditMultiple });
    }

    submitCSV = async () => {
        const { csvText } = this.state;
        const { productStore } = this.props.stores;
        this.setState({ multipleError: false });
        const response = await productStore.insertFromCSV(csvText);
        if (response?.status === 202) {
            this.setState({ csvText: '', multipleUpdated: response?.data || ''  });
            setTimeout(() => {
                this.loadAll(this.props, false);
            }, 1000);
        } else {
            // The store only returns a response on 202; anything else (e.g. a 500 from the
            // backend) used to leave the page silent (TICKET-01746).
            this.setState({ multipleError: true });
        }
    }

    updateCSV = async () => {
        const { csvText } = this.state;
        const { productStore } = this.props.stores;
        this.setState({ multipleError: false });
        const response = await productStore.updateFromCSV(csvText);
        if (response?.status === 202) {
            this.setState({ csvText: '', multipleUpdated: response?.data || '' });
            setTimeout(() => {
                this.loadAll(this.props, false);
            }, 1000);
        } else {
            this.setState({ multipleError: true });
        }
    }

    updateCsvText = async e => {
        this.setState({ csvText: e.target.value });
        const { productStore } = this.props.stores;
        const response = await productStore.loadFromCsv(e.target.value);
        const existingProducts = response?.data || [];
        const headerRow = response?.included?.headerRow || [];
        this.setState({ existingProducts, headerRow });
    }

    getColumnName = (idx) => {
        const { headerRow = [] } = this.state;
        return headerRow[idx];
    }

    productExistsByIdOrProductNumber = ({ id, productNumber }) => {
        const { existingProducts = [] } = this.state;
        if (id) {
            const productsWithSameIdExists = existingProducts.some(e => e.id === parseInt(id, 10));
            return productsWithSameIdExists;
        }
        if (productNumber) {
            const productsWithSameProductNumberExists = existingProducts.some(e => e.productNumber === productNumber);
            return productsWithSameProductNumberExists;
        }
        return false;
    }

    productExists = (line) => {
        // line.split('\t').map((e, idx) => {
        const cols = line.split('\t');
        const exists = cols.some((e, idx) => {
            const columnName = this.getColumnName(idx);
            if (columnName === 'id' || columnName === 'productNumber') {
                return this.productExistsByIdOrProductNumber({ [columnName]: e });
            }
        });
        return exists;
    }

    setSort = e => {
        const { name } = e.target.closest('th').dataset;
        const { sort } = this.state;
        this.setState({ sort: sort === name ? `-${name}` : name }, () => {
            this.loadAll(this.props);
        });
    }

    onClickPrintBarcode = e => {
        e.preventDefault();
        e.stopPropagation();
        const { gtin, productnumber, name, price = 0, vat = 0 } = e.target.closest('button').dataset;
        // console.log('onClickPrintSale', e);
        const { userStore } = this.props.stores;
        const { user } = userStore;
        const { language = 'no' } = user;
        const languageDef = countryMap[language || 'default'];

        // Calculate price including VAT
        const priceNum = parseFloat(price) || 0;
        const vatNum = parseFloat(vat) || 0;
        const priceIncludingVat = priceNum * (1 + vatNum / 100);

        const code = gtin || productnumber;

        // Label printer (Brother QL-820NWB and friends): the sheet IS the label,
        // so the label has to fill the page instead of sitting in the top-left
        // corner of it. Nothing here is tied to one roll size — every row is a
        // proportional slice of the page height and every element scales to its
        // slice, so the same markup fills 62x29mm, 60x86mm or 62x100mm.
        // The rows add up to 100 for readability: bars 50, code 11, name 17, price 22.
        // Each row also has a max-height cap: on tall rolls the proportional
        // slices would otherwise stretch and leave big gaps between the lines.
        // With the caps the content packs into a compact ~33mm block at the top
        // of the label (fits store shelf-edge display holders) and any surplus
        // label length stays blank at the bottom.
        const node = <IntlProvider definition={languageDef}>
            <div class='printLabel' style='padding: 1.5mm;'>
                <div style='flex: 50 1 0; min-height: 0; max-height: 14mm;'>
                    <Barcode
                        value={code}
                        barWidth={2}
                        barHeight={100}
                        showText={false}
                        margin={0}
                        marginLeft={20}
                        marginRight={20}
                        style='width: 100%; height: 100%;'
                        preserveAspectRatio='none'
                    />
                </div>
                <div style='flex: 11 1 0; min-height: 0; max-height: 4mm; padding-top: 0.5mm;'>
                    <FitTextSvg text={code} align='center' />
                </div>
                <div style='flex: 17 1 0; min-height: 0; max-height: 5mm; padding-top: 0.5mm;'>
                    <FitTextSvg text={`${name}`} align='left' />
                </div>
                <div style='flex: 22 1 0; min-height: 0; max-height: 8mm; padding-top: 0.5mm;'>
                    <FitTextSvg text={`kr.${util.format(priceIncludingVat, 2, ',', ' ')}`} align='right' fontWeight='bold' />
                </div>
            </div>
        </IntlProvider>;

        // Append to document temporarily to render Preact components
        const tempDiv = document.createElement('div');
        document.body.appendChild(tempDiv);
        render(node, tempDiv);
        const htmlContent = tempDiv.innerHTML;
        document.body.removeChild(tempDiv);
        render(null, tempDiv);
        printLabelViaIframe(htmlContent);
    }

    onClickPrintQrcode = e => {
        e.preventDefault();
        e.stopPropagation();
        const { id, name, productnumber, price = 0, vat = 0 } = e.target.closest('button').dataset;
        // console.log('onClickPrintSale', e);
        const { userStore, appState } = this.props.stores;
        const { currentCustomer = {} } = appState;
        const { hostname } = currentCustomer;

        const { user } = userStore;
        const { language = 'no' } = user;
        const languageDef = countryMap[language || 'default'];

        // Calculate price including VAT, same as the barcode label above:
        // the label shows the shelf price the customer pays, not the net price.
        const priceNum = parseFloat(price) || 0;
        const vatNum = parseFloat(vat) || 0;
        const priceIncludingVat = priceNum * (1 + vatNum / 100);

        // A QR has to stay square, so it gets `meet` rather than being stretched
        // like the barcode above. printLabel-split then puts it beside the text
        // on a landscape roll and above it on a portrait one, so a square code
        // never leaves two thirds of the label empty.
        const node = <IntlProvider definition={languageDef}>
            <div class='printLabel printLabel-split' style='padding: 1.5mm;'>
                <div class='printLabel-media'>
                    <QrCode
                        value={`${hostname === 'localhost' ? 'http' : 'https'}://${hostname}/#/products/pick/${id}`}
                        size={210}
                        level='H'
                        colorDark='#000000'
                        colorLight='#ffffff'
                        style='display: block; width: 100%; height: 100%;'
                        preserveAspectRatio='xMidYMid meet'
                    />
                </div>
                <div class='printLabel-body'>
                    <div style='flex: 22 1 0; min-height: 0;'>
                        <FitTextSvg text={`${productnumber}`} align='center' />
                    </div>
                    <div style='flex: 33 1 0; min-height: 0; padding-top: 0.5mm;'>
                        <FitTextSvg text={`${name}`} align='left' />
                    </div>
                    <div style='flex: 45 1 0; min-height: 0; padding-top: 0.5mm;'>
                        <FitTextSvg text={`kr.${util.format(priceIncludingVat, 2, ',', ' ')}`} align='right' fontWeight='bold' />
                    </div>
                </div>
            </div>
        </IntlProvider>;

        // Append to document temporarily to render Preact components
        const tempDiv = document.createElement('div');
        document.body.appendChild(tempDiv);
        render(node, tempDiv);
        const htmlContent = tempDiv.innerHTML;
        document.body.removeChild(tempDiv);
        render(null, tempDiv);
        printLabelViaIframe(htmlContent);
    }

    // "Handlekurv-QR": same label layout as the stock-pick QR above, but the
    // code points at /cart/add/:uuidv4 so an end user who scans it gets the
    // product added to their basket (login first if needed).
    onClickPrintCartQrcode = e => {
        e.preventDefault();
        e.stopPropagation();
        const { uuidv4, name, productnumber, price = 0, vat = 0 } = e.target.closest('button').dataset;
        if (!uuidv4) {
            return;
        }
        const { userStore, appState } = this.props.stores;
        const { currentCustomer = {} } = appState;
        const { hostname } = currentCustomer;

        const { user } = userStore;
        const { language = 'no' } = user;
        const languageDef = countryMap[language || 'default'];

        const priceNum = parseFloat(price) || 0;
        const vatNum = parseFloat(vat) || 0;
        const priceIncludingVat = priceNum * (1 + vatNum / 100);

        const node = <IntlProvider definition={languageDef}>
            <div class='printLabel printLabel-split' style='padding: 1.5mm;'>
                <div class='printLabel-media'>
                    <QrCode
                        value={cartAddUrl(hostname, uuidv4)}
                        size={210}
                        level='H'
                        colorDark='#000000'
                        colorLight='#ffffff'
                        style='display: block; width: 100%; height: 100%;'
                        preserveAspectRatio='xMidYMid meet'
                    />
                </div>
                <div class='printLabel-body'>
                    <div style='flex: 18 1 0; min-height: 0;'>
                        <FitTextSvg text={`${productnumber}`} align='center' />
                    </div>
                    <div style='flex: 30 1 0; min-height: 0; padding-top: 0.5mm;'>
                        <FitTextSvg text={`${name}`} align='left' />
                    </div>
                    <div style='flex: 36 1 0; min-height: 0; padding-top: 0.5mm;'>
                        <FitTextSvg text={`kr.${util.format(priceIncludingVat, 2, ',', ' ')}`} align='right' fontWeight='bold' />
                    </div>
                    <div style='flex: 16 1 0; min-height: 0; padding-top: 0.5mm;'>
                        <FitTextSvg text={languageDef?.dyrejournal?.['cart-qr-scan-to-buy'] || 'Scan to buy'} align='center' />
                    </div>
                </div>
            </div>
        </IntlProvider>;

        const tempDiv = document.createElement('div');
        document.body.appendChild(tempDiv);
        render(node, tempDiv);
        const htmlContent = tempDiv.innerHTML;
        document.body.removeChild(tempDiv);
        render(null, tempDiv);
        printLabelViaIframe(htmlContent);
    }

    onClickShowPurchaseDetails = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('button').dataset;
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightMediumLarge } = appState;
        // Show purchase details for the selected product
        appState.openDrawer('productPurchaseDetails', {
			height: drawerHeightMediumLarge,
            id: parseInt(id, 10),
		}, drawerLevel + 2);
    }

    onImageBadgeMouseEnter = (e) => {
        const { id } = e.currentTarget.dataset;
        const { productStore } = this.props.stores;
        const { products } = productStore;
        const product = products.find(p => p.id === parseInt(id, 10));
        if (!product?.images?.length) {
            return;
        }
        const rect = e.currentTarget.getBoundingClientRect();
        const popupWidth = 250;
        const popupHeight = 250;
        const margin = 10;
        let left = rect.left;
        let top = rect.bottom + 6;
        if (left + popupWidth > window.innerWidth - margin) {
            left = Math.max(margin, window.innerWidth - popupWidth - margin);
        }
        if (top + popupHeight > window.innerHeight - margin) {
            top = Math.max(margin, rect.top - popupHeight - 6);
        }
        this.setState({ hoverImageProduct: product, hoverImagePos: { left, top } });
    }

    onImageBadgeMouseLeave = () => {
        this.setState({ hoverImageProduct: null });
    }

    onClickAddProductStock = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('button, tr').dataset;
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightSmall } = appState;
        appState.openDrawer('productStockAdjustment', {
            height: drawerHeightSmall,
            id: parseInt(id, 10),
            callback: () => {
                this.setFocusToTable();
            },
        }, drawerLevel + 2);
    }

    selectCell = (e) => {
        const { col, row } = e.target.closest('td').dataset;
        this.setState({ selectedCell: { col: parseInt(col, 10), row: parseInt(row, 10) } });
    }

    keyNavigations = (e) => {
        const { selectedCell } = this.state;

        if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) {
            if (e.key === 'Enter') {
                // Handle enter key to edit the cell
                this.setState({ editingCell: { col: 0, row: 0 } });
                this.tbodyRef.focus();
            } else if (e.key === 'Escape') {
                // Handle escape key to cancel editing the cell
                this.setState({ editingCell: { col: 0, row: 0 } });
                this.tbodyRef.focus();
            }
            // Stop arrow keys from propagating when inside input elements
            if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                e.stopPropagation();
            }
            return;  // Ignore the keyDown if inside these elements
        }

        e.preventDefault();  // Prevent default browser behavior
        e.stopPropagation();  // Prevent event from bubbling up to parent components (pagination)

        if (e.key === 'ArrowDown') {
            this.setState({ selectedCell: { col: selectedCell.col, row: selectedCell.row + 1 } });
        } else if (e.key === 'ArrowUp') {
            const newRow = selectedCell.row - 1 < 0 ? 0 : selectedCell.row - 1;
            this.setState({ selectedCell: { col: selectedCell.col, row: newRow } });
        } else if (e.key === 'ArrowRight') {
            // Max col should be the number of columns - 1
            const maxCol = 20; // Update this to the actual number of columns - 1
            this.setState({ selectedCell: { col: Math.min(selectedCell.col + 1, maxCol), row: selectedCell.row } });
        } else if (e.key === 'ArrowLeft') {
            const newCol = selectedCell.col - 1 < 0 ? 0 : selectedCell.col - 1;
            this.setState({ selectedCell: { col: newCol, row: selectedCell.row } });
        } else if (e.key === 'Enter') {
            // Handle enter key to edit the cell or to run a function for this cell
            const { col, row } = selectedCell;
            // Get the cell from the DOM. Each cell contains data-col data-row attributes
            const cell = this.tbodyRef.querySelector(`td[data-col="${col}"][data-row="${row}"]`);
            // console.log('cell', cell);
            const { func, runclick } = cell.dataset;
            // Get closest tr
            const rowElement = cell.closest('tr');
            // console.log('rowElement', rowElement);
            // console.log('rowElement.dataset', rowElement.dataset);
            if (this.functions[func]) {
                this.functions[func](rowElement.dataset);
            }
            if (runclick) {
                // Trigger click on the cell
                cell.click();
            }
            // Handle enter key to edit the cell
            this.setState({ editingCell: { col: selectedCell.col, row: selectedCell.row } });
        } else if (e.key === 'Escape') {
            // Handle escape key to cancel editing the cell
            this.setState({ editingCell: { col: 0, row: 0 } });
            this.tbodyRef.focus();
        }
    }

    toggleLessThanIdealStock = () => {
        const { lessThanIdealStock } = this.state;
        this.setState({
            lessThanIdealStock: !lessThanIdealStock
        }, () => {
            this.loadAll();
        });
    }

    toggleHasJournal = () => {
        const { filterHasJournal } = this.state;
        this.setState({
            filterHasJournal: !filterHasJournal,
        }, () => {
            this.loadAll();
        });
    }

    toggleHasDrug = () => {
        const { filterHasDrug } = this.state;
        this.setState({
            filterHasDrug: !filterHasDrug,
        }, () => {
            this.loadAll();
        });
    }

    scanBarcode = () => {
        const { drawerLevel = 1 } = this.props;
		const { appState } = this.props.stores;
		const { drawerHeightMedium } = appState;

		appState.openDrawer('scanBarcode', {
			height: drawerHeightMedium,

            callback: data => {
                this.scanBarcodeCallback(data);
                // console.log('scanBarcode callback', data);
                appState.toggleDrawer(false, drawerLevel + 1);
            },
		}, drawerLevel + 1);
	}

    scanBarcodeCallback = async (data) => {
        // console.log('scanBarcodeCallback', data);
        if (!data?.data) {
            return;
        }
        // Search for product with barcode
        // const { productStore } = this.props.stores;
        // const product = await productStore.findByBarcode(data?.data);
        // if (product) {
        //     this.searchProductCallback(product);
        // }
        const search = data?.data;
        this.setState({ search });
        clearTimeout(this.searchTimer);
        this.doSearch(search);
    }

    componentDidMount() {
        this.loadColumnPreferences();
        this.loadAll();
        // Load dealers for filter dropdown
        const { dealerStore } = this.props.stores;
        dealerStore.loadPrefixes();
    }

    componentDidUpdate() {
        // Reload column preferences when user settings become available
        if (!this.columnPreferencesLoaded) {
            const { userStore } = this.props.stores;
            const { user = {} } = userStore;
            if (user.id && user.settings) {
                this.columnPreferencesLoaded = true;
                this.loadColumnPreferences();
            }
        }
    }

    componentWillUnmount() {
        clearTimeout(this.reloadTimer);
    }

    renderAccountingPlan = () => {
        const { productStore, userStore } = this.props.stores;
        const { accountingPlan = {} } = productStore;
        const {
            mainAccounts = [], genericAccounts = [], totalProducts = 0,
        } = accountingPlan;

        const renderSection = (titleId, titleDefault, rows) => (
            <div class='mb-3'>
                <div class='fw-bold mb-2'>
                    <Text id={titleId}>{titleDefault}</Text>
                </div>
                {rows.length === 0 ? (
                    <div class='text-body-secondary small'>
                        <Text id='product.accounting-plan-empty'>No products found</Text>
                    </div>
                ) : (
                    <div class='list-group'>
                        {rows.map(row => (
                            <div key={row.account} class='list-group-item d-flex justify-content-between align-items-center'>
                                <span>
                                    <span class='fw-bold me-2'>{row.account}</span>
                                    <span class='text-body-secondary'>{getAccountName(row.account, userStore.customer)}</span>
                                </span>
                                <span class='badge bg-secondary rounded-pill'>
                                    <Text id='product.accounting-plan-count' fields={{ count: row.count }}>
                                        {`${row.count} products`}
                                    </Text>
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );

        return (
            <>
                <div class='text-body-secondary small mb-2'>
                    <i class='fa-solid fa-list-ol me-1' />
                    <Text id='product.accounting-plan-total' fields={{ count: totalProducts }}>
                        {`${totalProducts} products in plan`}
                    </Text>
                </div>
                {renderSection('product.accounting-plan-main', 'Main accounts', mainAccounts)}
                {renderSection('product.accounting-plan-generic', 'Generic accounts', genericAccounts)}
            </>
        );
    }

    render() {
        const {
            productTitle,
            searchTitle,
            adjustPricesTitle,
            productGroupTitle,
            productCategoryTitle,
            skipChooseProduct = false,
        } = this.props;
        const {
            search,
            productGroup,
            productCategory,
            showEditMultiple,
            csvText,
            multipleUpdated = '',
            multipleError = false,
            sort,
            existingProducts = [],
            selectedCell = {},
            editingCell = {},
            lessThanIdealStock,
            showColumnConfig,
            hoverImageProduct,
            hoverImagePos,
            showProductStats,
            showAccountingPlan,
            showDealers,
            filterDealer,
            filterHasJournal,
            filterHasDrug,
            isBulkMatchingTripletex,
            bulkTripletexMatches,
            tripletexMatchError,
            expandedTripletexProductId,
            isSyncingBreece,
            breeceSyncResult,
            breeceSyncError,
            showCsvPriceUpload,
            showProductLibrary,
            showPriceAdjustmentWizard,
            wizardStep,
            wizardMode,
            wizardPercent,
            wizardAmount,
            wizardMargin,
            wizardRounding,
            wizardRoundIncVat,
            wizardComponents,
            wizardAccount,
            wizardCategory,
            wizardSearch,
            wizardPreview,
            wizardPreviewMessage,
            wizardPreviewError,
            wizardIsLoading,
            wizardApplyDone,
        } = this.state;
        const { appState, userStore, productStore, saleStore, dealerStore } = this.props.stores;

        const { user, isAdmin, isCustomerAdmin, customer } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');

        // Check for feature flags
        const { featureFlags = {} } = customer || {};
        const { tripletex, breece } = featureFlags;
        const showTripletex = tripletex > 0;
        const showBreece = breece > 0 && (isCustomerAdmin || isAdmin);

        const { products, total, limit, currentPage, productStats, productWeeklyStats, productStatsSummary, employeePickStats, employeePickWeeklyStats, stockPredictions, tableWeeklyStats } = productStore;
        const { dealers = [] } = dealerStore;

        const apiServer = util.getApiServer();
        const jwtToken = util.getJwtToken();

        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100' style='padding-top: 0px; padding-bottom: 100px;'>

                    <div class='font-weight-lighter px-3 box-header'>
                        <span>
                            <Text id='admin.product-list'>Product list</Text>
                        </span>
                    </div>

                    {/* Toggle Buttons Row */}
                    <div class='px-3 mb-3 d-flex flex-row gap-2 flex-wrap'>
                        {/* Product Movement Stats Toggle */}
                        <button
                            type='button'
                            class={`btn btn-sm ${showProductStats ? 'btn-primary' : 'btn-outline-primary'} rounded-pill`}
                            onClick={this.onToggleProductStats}
                        >
                            <i class='fa-solid fa-chart-line me-1' />
                            <Text id='product.movement-stats'>Product Movement</Text>
                            <i class={`fa-solid fa-chevron-${showProductStats ? 'up' : 'down'} ms-2`} />
                        </button>

                        {/* Accounting Plan Toggle */}
                        <button
                            type='button'
                            class={`btn btn-sm ${showAccountingPlan ? 'btn-primary' : 'btn-outline-primary'} rounded-pill`}
                            onClick={this.onToggleAccountingPlan}
                        >
                            <i class='fa-solid fa-list-ol me-1' />
                            <Text id='product.accounting-plan'>Accounting plan</Text>
                            <i class={`fa-solid fa-chevron-${showAccountingPlan ? 'up' : 'down'} ms-2`} />
                        </button>

                        {/* Dealers Section Toggle */}
                        <DealersSection
                            stores={this.props.stores}
                            buttonOnly
                            showDealers={showDealers}
                            onToggleDealers={this.onToggleDealers}
                        />
                    </div>

                    {showAccountingPlan && (
                        <div class={`d-flex flex-column rounded-lg mx-3 mb-3 p-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                            {this.renderAccountingPlan()}
                        </div>
                    )}

                    {showProductStats && (
                        <div class={`d-flex flex-column rounded-lg mx-3 mb-3 p-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                            {/* Period Info */}
                            <div class='text-muted small mb-2'>
                                <i class='fa-solid fa-calendar-range me-1' />
                                <Text id='product.stats-period'>Statistics for the last 12 months</Text>
                                {' '}({util.isoDate(new Date(Date.now() - 365 * 24 * 60 * 60 * 1000), false, false, true)} - {util.isoDate(new Date(), false, false, true)})
                            </div>
                            {/* Summary Stats Row */}
                            <div class='d-flex flex-row gap-4 mb-3'>
                                <div class='text-center'>
                                    <div class='text-muted small'><Text id='product.total-sold'>Total Products Sold</Text></div>
                                    <div class='h4 text-primary'>{util.format(productStatsSummary?.totalProductsSold || 0, 0)}</div>
                                </div>
                                <div class='text-center'>
                                    <div class='text-muted small'><Text id='product.employee-picks'>Employee Picks</Text></div>
                                    <div class='h4 text-warning'>{util.format(productStatsSummary?.totalEmployeePicks || 0, 0)}</div>
                                </div>
                                <div class='text-center'>
                                    <div class='text-muted small'><Text id='product.regular-sales'>Regular Sales</Text></div>
                                    <div class='h4 text-success'>{util.format(productStatsSummary?.totalRegularSales || 0, 0)}</div>
                                </div>
                                <div class='text-center'>
                                    <div class='text-muted small'><Text id='product.avg-per-week'>Avg/Week</Text></div>
                                    <div class='h4 text-info'>{util.format(productStatsSummary?.avgPerWeek || 0, 0)}</div>
                                </div>
                            </div>

                            {/* Hottest Products Section */}
                            <div class='font-weight-lighter box-header mb-2'>
                                <i class='fa-solid fa-fire text-danger me-1' />
                                <Text id='product.hottest-products'>Hottest Products (Fastest Moving)</Text>
                            </div>
                            <div class='row'>
                                {productStats && productStats.slice(0, 6).map((product, idx) => {
                                    // Transform weekly stats for this product
                                    const productWeeklyData = (productWeeklyStats || [])
                                        .filter(stat => stat.productId === product._id)
                                        .sort((a, b) => a.year !== b.year ? a.year - b.year : a.week - b.week)
                                        .map((stat, index) => ({
                                            x: index,
                                            y: stat.qty,
                                            label: `${stat.year}-W${stat.week}`,
                                        }));
                                    return (
                                        <div class='col-md-4 col-lg-2 mb-2' key={product._id}>
                                            <div class={`p-2 rounded ${darkmode ? 'bg-dark' : 'bg-light'}`}>
                                                <div class='d-flex justify-content-between align-items-center mb-1'>
                                                    <small class='text-truncate' style='max-width: 100px;' title={product.productName}>
                                                        {product.productName || `Product ${product._id}`}
                                                    </small>
                                                    <span class='badge bg-danger'>{(product.velocityPerWeek || 0).toFixed(1)}/w</span>
                                                </div>
                                                <MiniGraph
                                                    stores={this.props.stores}
                                                    dataLeft={[productWeeklyData]}
                                                    width={120}
                                                    height={30}
                                                    showXTicks={0}
                                                    showYTicksLeft={0}
                                                    paddingLeft={2}
                                                    paddingTop={2}
                                                    smoothLeft={[true]}
                                                    gradientFillLeft={['#ef4444']}
                                                    gradientOpacity={0.2}
                                                    inputStyleLeft={'stroke: #ef4444 !important; stroke-width: 1px;'}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Employee Picks Section */}
                            {employeePickStats && employeePickStats.length > 0 && (
                                <>
                                    <div class='font-weight-lighter box-header mb-2 mt-3'>
                                        <i class='fa-solid fa-user-tag text-warning me-1' />
                                        <Text id='product.employee-picks-hottest'>Hottest Employee Picks</Text>
                                    </div>
                                    <div class='row'>
                                        {employeePickStats.slice(0, 6).map((product, idx) => {
                                            const productWeeklyData = (employeePickWeeklyStats || [])
                                                .filter(stat => stat.productId === product._id)
                                                .sort((a, b) => a.year !== b.year ? a.year - b.year : a.week - b.week)
                                                .map((stat, index) => ({
                                                    x: index,
                                                    y: stat.qty,
                                                    label: `${stat.year}-W${stat.week}`,
                                                }));
                                            return (
                                                <div class='col-md-4 col-lg-2 mb-2' key={product._id}>
                                                    <div class={`p-2 rounded ${darkmode ? 'bg-dark' : 'bg-light'}`}>
                                                        <div class='d-flex justify-content-between align-items-center mb-1'>
                                                            <small class='text-truncate' style='max-width: 100px;' title={product.productName}>
                                                                {product.productName || `Product ${product._id}`}
                                                            </small>
                                                            <span class='badge bg-warning text-dark'>{(product.employeePickVelocity || 0).toFixed(1)}/w</span>
                                                        </div>
                                                        <MiniGraph
                                                            stores={this.props.stores}
                                                            dataLeft={[productWeeklyData]}
                                                            width={120}
                                                            height={30}
                                                            showXTicks={0}
                                                            showYTicksLeft={0}
                                                            paddingLeft={2}
                                                            paddingTop={2}
                                                            smoothLeft={[true]}
                                                            gradientFillLeft={['#f59e0b']}
                                                            gradientOpacity={0.2}
                                                            inputStyleLeft={'stroke: #f59e0b !important; stroke-width: 1px;'}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}

                            {/* Stock Predictions Section */}
                            {stockPredictions && stockPredictions.length > 0 && (
                                <>
                                    <div class='font-weight-lighter box-header mb-2 mt-3'>
                                        <i class='fa-solid fa-triangle-exclamation text-danger me-1' />
                                        <Text id='product.stock-alert'>Stock Alert - Running Low</Text>
                                    </div>
                                    <div class='row'>
                                        {stockPredictions.slice(0, 6).map((product, idx) => {
                                            const urgencyClass = product.weeksUntilStockout <= 2 ? 'bg-danger' :
                                                product.weeksUntilStockout <= 4 ? 'bg-warning text-dark' : 'bg-info';
                                            return (
                                                <div class='col-md-4 col-lg-2 mb-2' key={product._id}>
                                                    <div class={`p-2 rounded ${darkmode ? 'bg-dark' : 'bg-light'}`}>
                                                        <div class='d-flex justify-content-between align-items-center mb-1'>
                                                            <small class='text-truncate' style='max-width: 80px;' title={product.productName}>
                                                                {product.productName || `Product ${product._id}`}
                                                            </small>
                                                            <span class={`badge ${urgencyClass}`}>{product.weeksUntilStockout}w</span>
                                                        </div>
                                                        <div class='d-flex justify-content-between small text-muted'>
                                                            <span><Text id='product.in-stock'>Stock</Text>: {product.inStock}</span>
                                                            <span>{(product.velocityPerWeek || 0).toFixed(1)}/w</span>
                                                        </div>
                                                        <div class='progress mt-1' style='height: 4px;'>
                                                            <div
                                                                class={`progress-bar ${product.weeksUntilStockout <= 2 ? 'bg-danger' : product.weeksUntilStockout <= 4 ? 'bg-warning' : 'bg-info'}`}
                                                                style={`width: ${Math.min(100, (product.inStock / (product.idealStock || product.inStock)) * 100)}%`}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* Dealers Section Content */}
                    <DealersSection
                        stores={this.props.stores}
                        contentOnly
                        showDealers={showDealers}
                        onToggleDealers={this.onToggleDealers}
                    />

                    <div class='w-100 d-flex flex-sm-row flex-column'>
                        <div class='w-100 d-flex flex-column'>
                            {/* <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between'>
                                <Text id='dyrejournal.all-products'>All products</Text>
                            </div> */}
                            <div class={`d-flex flex-column mx-3 px-3 py-2 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                <div class='d-flex flex-column justify-content-between'>
                                    {/* Filters row */}
                                    <div class='d-flex flex-wrap align-items-center gap-2'>
                                        <small class='text-body-secondary fw-bold'>
                                            <i class='fa-duotone fa-filter me-1' />
                                            <Text id='dyrejournal.wizard-filters-label'>Filtre</Text>:
                                        </small>
                                        <div class='input-group flex-nowrap' style='flex: 1 1 260px; min-width: 220px; width: auto;'>
                                            <div class='input-group-text rounded-pill-left'>
                                                <i class='fa-regular fa-magnifying-glass'></i>
                                            </div>
                                            <input
                                                class={`form-control ${!search ? 'rounded-none' : ''}`}
                                                type='text'
                                                value={search}
                                                placeholder={`${searchTitle}`}
                                                onInput={this.searchInput}
                                                onKeyDown={this.handleKeyDown}
                                            />
                                            <button class={`btn btn-sm btn-outline-secondary ${!search ? 'rounded-pill-right' : 'rounded-none'}`} type='button' onClick={this.scanBarcode}>
                                                <i class='fa-solid fa-barcode'></i>
                                            </button>
                                            {search && <>
                                                <button class='btn btn-danger btn-sm rounded-pill-right' type='button' onClick={this.clearSearch}>
                                                    <i class='fa-solid fa-circle-xmark'></i>
                                                </button>
                                            </>}
                                        </div>
                                        <select
                                            class='form-select form-select-sm rounded-pill'
                                            style='width: auto; max-width: 200px;'
                                            onInput={this.productGroupInput}
                                        >
                                            <option value=''>-- Velg konto --</option>
                                            {getAccountOptions(userStore.customer).map(([num, label]) => (
                                                <option value={num}>{label}</option>
                                            ))}
                                        </select>
                                        <select
                                            class='form-select form-select-sm rounded-pill'
                                            style='width: auto;'
                                            onInput={this.productCategoryInput}
                                        >
                                            <option value=''>-- Velge Kategori --</option>
                                            <option value='produkt'>Produkt</option>
                                            <option value='honorar'>Honorar</option>
                                            <option value='medisin'>Medisin</option>
                                            <option value='annet'>Annet</option>
                                        </select>

                                        {dealers.length > 0 && (
                                            <select
                                                class='form-select form-select-sm rounded-pill'
                                                style='width: auto; max-width: 200px;'
                                                onInput={this.dealerFilterInput}
                                                value={filterDealer}
                                            >
                                                <option value=''>-- Velg forhandler --</option>
                                                {dealers.map(d => (
                                                    <option key={d.id} value={d.id}>{d.name}</option>
                                                ))}
                                            </select>
                                        )}

                                        <select class='form-select form-select-sm rounded-pill text-center px-1' style='width: 90px;' onChange={this.onChooseLimit} value={limit}>
                                            <option value='10'>10</option>
                                            <option value='25'>25</option>
                                            <option value='50'>50</option>
                                            <option value='100'>100</option>
                                            <option value='250'>250</option>
                                            <option value='500'>500</option>
                                        </select>

                                        <button
                                            type='button'
                                            class={`btn btn-sm btn-${lessThanIdealStock ? '' : 'outline-'}secondary rounded-pill`}
                                            onClick={this.toggleLessThanIdealStock}
                                        >
                                            <nobr>
                                                <i class='fa-solid fa-filter me-1' /> &lt; <Text id='dyrejournal.product.showIdealStockLow'> ideal stock</Text>
                                            </nobr>
                                        </button>
                                        <button
                                            type='button'
                                            class={`btn btn-sm btn-${filterHasJournal ? '' : 'outline-'}success rounded-pill`}
                                            onClick={this.toggleHasJournal}
                                        >
                                            <nobr>
                                                <i class='fa-solid fa-wand-magic-sparkles me-1' /> <Text id='dyrejournal.product.journal-macro'>Journal Macro</Text>
                                            </nobr>
                                        </button>
                                        <button
                                            type='button'
                                            class={`btn btn-sm btn-${filterHasDrug ? '' : 'outline-'}primary rounded-pill`}
                                            onClick={this.toggleHasDrug}
                                        >
                                            <nobr>
                                                <i class='fa-solid fa-pills me-1' /> <Text id='dyrejournal.product.drug-connected'>Drug Connected</Text>
                                            </nobr>
                                        </button>
                                    </div>

                                    {/* Actions row */}
                                    <div class='d-flex flex-wrap align-items-center gap-2 mt-2 pt-2 border-top'>
                                        <small class='text-body-secondary fw-bold'>
                                            <i class='fa-duotone fa-screwdriver-wrench me-1' />
                                            <Text id='dyrejournal.wizard-actions-label'>Actions</Text>:
                                        </small>
                                        <a
                                            href={`${apiServer}${productStore.api?.load?.url}csv?jwtToken=${jwtToken}&account=${productGroup || ''}&category=${productCategory || ''}&search=${search || ''}&lessThanIdealStock=${lessThanIdealStock || ''}`}
                                            target='_blank'
                                            class='btn btn-sm btn-outline-primary rounded-pill'
                                        >
                                            <nobr><i class='fa-duotone fa-download' /> <Text id='dyrejournal.download-csv'>Download CSV</Text></nobr>
                                        </a>
                                        {showTripletex && <>
                                            <button
                                                class={`btn btn-sm ${tripletexMatchError ? 'btn-danger' : 'btn-outline-info'} rounded-pill`}
                                                type='button'
                                                onClick={this.onClickBulkMatchTripletex}
                                                disabled={isBulkMatchingTripletex}
                                            >
                                                <nobr>
                                                    <i class={`fa-duotone fa-${isBulkMatchingTripletex ? 'spinner fa-spin' : 'code-compare'}`} />
                                                    {' '}Tripletex sjekk
                                                    {Object.keys(bulkTripletexMatches).length > 0 &&
                                                        <span class='badge bg-success ms-1'>{Object.keys(bulkTripletexMatches).length}</span>
                                                    }
                                                </nobr>
                                            </button>
                                            {(Object.keys(bulkTripletexMatches).length > 0 || tripletexMatchError) &&
                                                <button class='btn btn-sm btn-outline-secondary rounded-pill' type='button' onClick={this.clearBulkTripletexMatches}>
                                                    <i class='fa-solid fa-times' />
                                                </button>
                                            }
                                        </>}
                                        {showBreece && <>
                                            <button
                                                class={`btn btn-sm ${breeceSyncError ? 'btn-danger' : (breeceSyncResult ? 'btn-success' : 'btn-outline-warning')} rounded-pill`}
                                                type='button'
                                                onClick={this.onClickSyncToBreece}
                                                disabled={isSyncingBreece}
                                                title='Sync current product list to Breece Cloud electronic shelf labels'
                                            >
                                                <nobr>
                                                    <i class={`fa-duotone fa-${isSyncingBreece ? 'spinner fa-spin' : 'tags'}`} />
                                                    {' '}Breece Cloud
                                                    {breeceSyncResult &&
                                                        <span class='badge bg-light text-dark ms-1'>{breeceSyncResult.totalSuccess}</span>
                                                    }
                                                </nobr>
                                            </button>
                                            <Tooltip
                                                width={300}
                                                text={<i class='fa-duotone fa-circle-info text-body-secondary' />}
                                            >
                                                <Text id='dyrejournal.breece-help'>Syncs the products currently shown (excluding offline products) to Breece Cloud electronic shelf labels. Prices include VAT, and campaign prices are used when a discount is active. Only the products loaded in the list are sent — increase the list limit to send more. If enabled, a nightly job syncs the full product catalog automatically.</Text>
                                            </Tooltip>
                                            {(breeceSyncResult || breeceSyncError) &&
                                                <button class='btn btn-sm btn-outline-secondary rounded-pill' type='button' onClick={this.clearBreeceSyncResult}>
                                                    <i class='fa-solid fa-times' />
                                                </button>
                                            }
                                        </>}
                                        <button
                                            class={`btn btn-sm btn-${showEditMultiple ? '' : 'outline-'}secondary rounded-pill`}
                                            type='button'
                                            onClick={this.onClickToggleMultiple}
                                        >
                                            <nobr><i class='fa-duotone fa-edit' /> <Text id='dyrejournal.edit-multople'>Edit multiple</Text></nobr>
                                        </button>
                                        <button
                                            type='button'
                                            class={`btn btn-sm btn-${showCsvPriceUpload ? '' : 'outline-'}success rounded-pill`}
                                            onClick={this.toggleCsvPriceUpload}
                                            title='Upload supplier price list (CSV)'
                                        >
                                            <nobr>
                                                <i class='fa-solid fa-file-csv' /> <Text id='dyrejournal.product.upload-price-list'>Upload Price List</Text>
                                            </nobr>
                                        </button>
                                        <button type='button' class={`btn btn-sm btn-${showProductLibrary ? '' : 'outline-'}info rounded-pill`}
                                            onClick={this.toggleProductLibrary} title='Importer fra Dyrejournals varelister'>
                                            <nobr>
                                                <i class='fa-solid fa-book' /> <Text id='dyrejournal.product.import-from-library'>Product library</Text>
                                            </nobr>
                                        </button>
                                        <button
                                            type='button'
                                            class='btn btn-sm btn-outline-secondary rounded-pill'
                                            // onClick={this.onClickAllProducts}
                                            onClick={this.onClickNewProduct}
                                        >
                                            <nobr>
                                                {/* <i class='fa-solid fa-list text-muted' /> <Text id='dyrejournal.product.all'>All Products</Text> */}
                                                <i class='fa-solid fa-plus text-muted' /> <Text id='dyrejournal.product.new'>New product</Text>
                                            </nobr>
                                        </button>
                                        <button
                                            type='button'
                                            class={`btn btn-sm btn-${showPriceAdjustmentWizard ? '' : 'outline-'}primary rounded-pill`}
                                            onClick={this.togglePriceAdjustmentWizard}
                                        >
                                            <nobr>
                                                <i class='fa-solid fa-chart-line-up me-1' />
                                                Prisjustering
                                                <i class={`fa-solid fa-chevron-${showPriceAdjustmentWizard ? 'up' : 'down'} ms-2`} />
                                            </nobr>
                                        </button>

                                        <div class='position-relative'>
                                            <button
                                                type='button'
                                                class={`btn btn-sm btn-${showColumnConfig ? '' : 'outline-'}secondary rounded-pill`}
                                                onClick={this.toggleColumnConfig}
                                                title='Configure columns'
                                            >
                                                <nobr>
                                                    <i class='fa-solid fa-cog' />
                                                </nobr>
                                            </button>
                                            {showColumnConfig && (
                                                <div class={`position-absolute border rounded shadow-lg p-3 mt-1 ${darkmode ? 'bg-dark text-light' : 'bg-white'}`} style='z-index: 1000; right: 0; min-width: 200px; max-height: 400px; overflow-y: auto;'>
                                                    <div class='d-flex justify-content-between align-items-center mb-2 text-nowrap'>
                                                        <strong class='me-3'><Text id='dyrejournal.product.configure-columns'>Configure Columns</Text></strong>
                                                        <button type='button' class={`btn-close btn-sm ${darkmode ? 'btn-close-white' : ''}`} onClick={this.toggleColumnConfig}></button>
                                                    </div>
                                                    <hr class='my-2' />
                                                    {COLUMN_DEFINITIONS.map(col => (
                                                        <div class='form-check' key={col.key}>
                                                            <input
                                                                class='form-check-input'
                                                                type='checkbox'
                                                                id={`col-${col.key}`}
                                                                checked={this.isColumnVisible(col.key)}
                                                                onChange={() => this.toggleColumn(col.key)}
                                                            />
                                                            <label class='form-check-label' for={`col-${col.key}`}>
                                                                {col.label}
                                                            </label>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                    </div>

                                    {/* Tripletex Results Summary (moved out of the button row) */}
                                    {showTripletex && Object.keys(bulkTripletexMatches).length > 0 && (() => {
                                        // Calculate summary statistics
                                        const matchEntries = Object.entries(bulkTripletexMatches);
                                        const totalChecked = products?.length || 0;
                                        const linkedCount = products?.filter(p => p.tripletexProductId > 0).length || 0;
                                        const matchesWithDiscrepancies = matchEntries.filter(([_, m]) => m.discrepancies?.length > 0).length;
                                        const matchesWithoutDiscrepancies = matchEntries.filter(([_, m]) => !m.discrepancies || m.discrepancies.length === 0).length;
                                        const unmatchedCount = totalChecked - linkedCount - matchEntries.length;

                                        return (
                                            <div class='alert alert-info py-2 px-3 mt-2 mb-0 d-flex align-items-center flex-wrap'>
                                                <strong class='me-2'>Resultater:</strong>
                                                <span class='badge bg-success me-2' title='Allerede koblet til Tripletex'>{linkedCount} koblet</span>
                                                <span class='badge bg-primary me-2' title='Treff uten avvik'>{matchesWithoutDiscrepancies} treff</span>
                                                {matchesWithDiscrepancies > 0 && <span class='badge bg-warning text-dark me-2' title='Treff med avvik (MVA/Konto forskjeller)'>{matchesWithDiscrepancies} avvik</span>}
                                                <span class='badge bg-secondary' title='Ikke funnet i Tripletex'>{unmatchedCount < 0 ? 0 : unmatchedCount} ikke funnet</span>
                                            </div>
                                        );
                                    })()}
                                    {showTripletex && tripletexMatchError && (
                                        <div class='alert alert-danger py-2 px-3 mt-2 mb-0'>
                                            <i class='fa-solid fa-exclamation-triangle me-2' />
                                            {tripletexMatchError}
                                        </div>
                                    )}

                                    {showBreece && isSyncingBreece && (
                                        <div class='alert alert-info py-2 px-3 mt-2 mb-0 d-flex align-items-center'>
                                            <i class='fa-solid fa-spinner fa-spin me-2' />
                                            Syncing {products.length} products to Breece Cloud...
                                        </div>
                                    )}
                                    {showBreece && breeceSyncResult && !breeceSyncResult.totalFailed && (
                                        <div class='alert alert-success py-2 px-3 mt-2 mb-0 d-flex align-items-center'>
                                            <i class='fa-solid fa-check-circle me-2' />
                                            {breeceSyncResult.totalSuccess} products synced to Breece Cloud
                                        </div>
                                    )}
                                    {showBreece && breeceSyncResult && breeceSyncResult.totalFailed > 0 && (
                                        <div class='alert alert-warning py-2 px-3 mt-2 mb-0 d-flex align-items-center'>
                                            <i class='fa-solid fa-exclamation-triangle me-2' />
                                            {breeceSyncResult.totalSuccess} of {breeceSyncResult.totalProducts} products synced. {breeceSyncResult.totalFailed} failed.
                                        </div>
                                    )}
                                    {showBreece && breeceSyncError && (
                                        <div class='alert alert-danger py-2 px-3 mt-2 mb-0 d-flex align-items-center'>
                                            <i class='fa-solid fa-exclamation-circle me-2' />
                                            {breeceSyncError}
                                        </div>
                                    )}

                                    {showEditMultiple && <div class='d-flex flex-column justify-content-between bg-warning-light rounded-lg mt-2 mb-5 pb-3'>
                                        <div class='w-100 d-flex flex-column justify-content-start'>
                                            <Input
                                                stores={this.props.stores}
                                                holdingStore={productStore}
                                                object={{}}
                                                type='textarea'
                                                class='form-control'
                                                placeholder={`Paste CSV here...`}
                                                help={'Copy from Excel or Google Sheets and paste here without any formatting. First row is always the header row with the same names as the fields in the table.'}
                                                onInput={this.updateCsvText}
                                                inputValue={csvText}
                                                classNames='form-control'
                                            />
                                        </div>

                                        <div class='w-100 d-flex flex-row justify-content-center mt-3'>
                                            <div class='bg-danger text-white rounded-lg p-3' role='alert'>
                                                <div style='font-size: 1.5em;'>
                                                    <i class='fa-solid fa-triangle-exclamation' /> <Text id='dyrejournal.csv-warning'>Warning: This will update all products with info from the CSV file. Make sure you have the correct data before you proceed!</Text>
                                                </div>
                                                <div>
                                                    <ul>
                                                        <li><Text id='dyrejournal.csv-warning-1'>Id or productNumber is the unique identifier of the product and has to always be the same.</Text></li>
                                                        <li><Text id='dyrejournal.csv-warning-2'>If you leave a field empty, it will not be updated.</Text></li>
                                                        <li><Text id='dyrejournal.csv-warning-3'>If you accidentially damage your product database, we will have to charge you for disaster recovery.</Text></li>
                                                    </ul>
                                                </div>
                                                <div style='font-size: 1.5em;'>
                                                    <i class='fa-solid fa-triangle-exclamation' /> <Text id='dyrejournal.csv-warning-unsure'>If you are unsure about this, please stop now!</Text>
                                                </div>
                                            </div>
                                        </div>

                                        <div class='w-100 d-flex flex-row justify-content-center mt-3'>
                                            <button type='button' class='btn btn-sm btn-danger rounded-pill mx-1' onClick={this.submitCSV}>
                                                <i class='fa-duotone fa-upload me-2' />
                                                <Text id='input.admin.multi-upload'>Insert & update from CSV</Text>
                                            </button>
                                            <button type='button' class='btn btn-sm btn-success rounded-pill mx-1' onClick={this.updateCSV}>
                                                <i class='fa-duotone fa-pen-to-square me-2' />
                                                <Text id='input.admin.multi-update'>Update from CSV</Text>
                                            </button>
                                        </div>

                                        {multipleError && <div class='alert alert-danger mt-2' role='alert'>
                                            <i class='fa-duotone fa-triangle-exclamation' /> <Text id='dyrejournal.multiple-failed'>The update failed and nothing was changed. Check the pasted rows (the first row must be the header row) and try again.</Text>
                                        </div>}

                                        {multipleUpdated && <div class='alert alert-success mt-2' role='alert'>
                                            <i class='fa-duotone fa-circle-check' /> <Text id='dyrejournal.multiple-updated'>Multiple accepted</Text><br />
                                            <Text id='dyrejournal.multiple-updated'>Updated:</Text> {multipleUpdated?.objectsForUpdate?.length || 0}<br />
                                            <Text id='dyrejournal.multiple-inserted'>Inserted:</Text> {multipleUpdated?.objectsForInsert?.length || 0}<br />
                                            {/* {JSON.stringify(multipleUpdated)} */}
                                            {/* {"objectsForUpdate":[{"id":5297,"productNumber":"100845","type":"service","category":"","name":"273 · kontroll med sedasjon (uten med)","description":"Dette er en test","qty":12,"inStock":"","account":3010,"price":0,"inPrice":"","vat":25},{"id":5261,"productNumber":"100800","type":"service","category":"","name":"496 · tannsliping av kinntenner kanin/marsvin uten sedasjon","description":"","qty":"","inStock":"","account":3010,"price":0,"inPrice":"","vat":25}]} */}
                                        </div>}

                                        {csvText && <>
                                            <div class='table-responsive'>
                                                <table class='table table-striped mt-3'>
                                                    <thead>
                                                        <tr>
                                                            <th><Text id='dyrejournal.multiple-exists'>Exists</Text></th>
                                                            {csvText.split('\n')[0].split('\t').map(e => {
                                                                return (<th>{e}</th>);
                                                            })}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {csvText.split('\n').slice(1).map(line => {
                                                            const productExists = this.productExists(line);
                                                            return (<tr>
                                                                <td>{productExists ? <i class='fa-solid fa-check text-success' /> : <i class='fa-solid fa-plus text-danger' />}</td>
                                                                {line.split('\t').map((e, idx) => {
                                                                    return (<td>{e}</td>);
                                                                })}
                                                            </tr>);
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </>}
                                    </div>}

                                    {showCsvPriceUpload && (
                                        <div class='mb-3'>
                                            <CsvPriceUpload
                                                stores={this.props.stores}
                                                onClose={this.toggleCsvPriceUpload}
                                                onComplete={this.onCsvPriceUploadComplete}
                                            />
                                        </div>
                                    )}

                                    {showProductLibrary && (
                                        <div class='mb-3 mx-3'>
                                            <ProductLibraryImport
                                                stores={this.props.stores}
                                                onClose={this.toggleProductLibrary}
                                                onComplete={() => { this.loadAll(this.props, false); }}
                                            />
                                        </div>
                                    )}

                                    {/* Price Adjustment Wizard */}
                                    {showPriceAdjustmentWizard && (
                                        <div class='card mt-3'>
                                            {/* Step progress indicator */}
                                            <div class='card-header bg-body-secondary'>
                                                <div class='d-flex align-items-center justify-content-center gap-2'>
                                                    {[
                                                        { step: 1, label: 'Velg felter' },
                                                        { step: 2, label: 'Filter' },
                                                        { step: 3, label: 'Simuler' },
                                                        { step: 4, label: 'Bekreft' },
                                                    ].map((s, idx) => (<>
                                                        {idx > 0 && <div class={`flex-grow-0 border-top ${wizardStep >= s.step ? 'border-primary' : 'border-secondary'}`} style='width: 40px; height: 0;' />}
                                                        <button
                                                            type='button'
                                                            class='btn btn-sm p-0 border-0 bg-transparent d-flex align-items-center gap-1'
                                                            onClick={() => {
                                                                if (s.step < wizardStep) this.wizardGoToStep(s.step);
                                                            }}
                                                            disabled={s.step > wizardStep}
                                                        >
                                                            <span class={`badge rounded-pill ${wizardStep === s.step ? 'bg-primary' : (wizardStep > s.step ? 'bg-success' : 'bg-secondary')}`}>
                                                                {wizardStep > s.step ? <i class='fa-solid fa-check' /> : s.step}
                                                            </span>
                                                            <small class={wizardStep === s.step ? 'text-body fw-bold' : 'text-body-secondary'}>{s.label}</small>
                                                        </button>
                                                    </>))}
                                                </div>
                                            </div>

                                            <div class='card-body'>
                                                {/* Step 1: Velg felter */}
                                                {wizardStep === 1 && (<>
                                                    <div class='row g-3'>
                                                        <div class='col-md-4'>
                                                            <label class='form-label fw-bold'>Modus</label>
                                                            <select
                                                                class='form-select'
                                                                value={wizardMode}
                                                                onChange={this.wizardModeChange}
                                                            >
                                                                <option value='percent'>Prosent %</option>
                                                                <option value='fixed'>Fast beløp kr</option>
                                                                <option value='margin'>Margin %</option>
                                                            </select>
                                                        </div>
                                                        <div class='col-md-4'>
                                                            <label class='form-label fw-bold'>
                                                                {wizardMode === 'percent' && 'Prosent'}
                                                                {wizardMode === 'fixed' && 'Beløp (kr)'}
                                                                {wizardMode === 'margin' && 'Margin (%)'}
                                                            </label>
                                                            {wizardMode === 'percent' && <input
                                                                class='form-control'
                                                                type='number'
                                                                step='any'
                                                                value={wizardPercent}
                                                                placeholder='f.eks. 10'
                                                                onInput={(e) => this.setState({ wizardPercent: e.target.value, wizardPreviewError: null })}
                                                            />}
                                                            {wizardMode === 'fixed' && <input
                                                                class='form-control'
                                                                type='number'
                                                                step='any'
                                                                value={wizardAmount}
                                                                placeholder='f.eks. 50'
                                                                onInput={(e) => this.setState({ wizardAmount: e.target.value, wizardPreviewError: null })}
                                                            />}
                                                            {wizardMode === 'margin' && <input
                                                                class='form-control'
                                                                type='number'
                                                                step='any'
                                                                value={wizardMargin}
                                                                placeholder='f.eks. 40'
                                                                onInput={(e) => this.setState({ wizardMargin: e.target.value, wizardPreviewError: null })}
                                                            />}
                                                        </div>
                                                        <div class='col-md-4'>
                                                            <label class='form-label fw-bold'>Avrunding</label>
                                                            <select
                                                                class='form-select'
                                                                value={wizardRounding}
                                                                onChange={(e) => this.setState({ wizardRounding: parseInt(e.target.value, 10) || 0 })}
                                                            >
                                                                <option value='0'>Ingen avrunding</option>
                                                                <option value='1'>Nærmeste 1 kr</option>
                                                                <option value='5'>Nærmeste 5 kr</option>
                                                                <option value='10'>Nærmeste 10 kr</option>
                                                                <option value='50'>Nærmeste 50 kr</option>
                                                                <option value='100'>Nærmeste 100 kr</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                    {wizardRounding > 0 && (
                                                        <div class='mt-3'>
                                                            <div class='form-check form-switch'>
                                                                <input
                                                                    class='form-check-input'
                                                                    type='checkbox'
                                                                    role='switch'
                                                                    id='wizard-round-inc-vat'
                                                                    checked={wizardRoundIncVat}
                                                                    onChange={() => this.setState(prevState => ({ wizardRoundIncVat: !prevState.wizardRoundIncVat }))}
                                                                />
                                                                <label class='form-check-label' for='wizard-round-inc-vat'>
                                                                    Rund av på pris <strong>inkl. mva</strong> (det kunden betaler)
                                                                </label>
                                                            </div>
                                                            <small class='text-body-secondary'>
                                                                {wizardRoundIncVat
                                                                    ? `Prisen kunden betaler blir runde tall (nærmeste ${wizardRounding} kr). Prisen eks. mva lagres med øre.`
                                                                    : `Prisen eks. mva blir runde tall (nærmeste ${wizardRounding} kr). Da blir prisen kunden betaler sjelden rund.`}
                                                            </small>
                                                        </div>
                                                    )}
                                                    <div class='mt-3'>
                                                        <label class='form-label fw-bold'>Komponenter</label>
                                                        <div class='d-flex flex-wrap gap-3'>
                                                            {['price', 'medicine', 'fee', 'equipment', 'other'].map(comp => (
                                                                <div class='form-check' key={comp}>
                                                                    <input
                                                                        class='form-check-input'
                                                                        type='checkbox'
                                                                        id={`wizard-comp-${comp}`}
                                                                        checked={wizardMode === 'margin' ? comp === 'price' : wizardComponents[comp]}
                                                                        disabled={wizardMode === 'margin'}
                                                                        onChange={() => this.wizardComponentToggle(comp)}
                                                                    />
                                                                    <label class='form-check-label' for={`wizard-comp-${comp}`}>
                                                                        {comp === 'price' ? 'Pris' : comp === 'medicine' ? 'Medisin' : comp === 'fee' ? 'Honorar' : comp === 'equipment' ? 'Utstyr' : 'Annet'}
                                                                    </label>
                                                                </div>
                                                            ))}
                                                        </div>
                                                        {wizardMode === 'margin' && (
                                                            <small class='text-body-secondary'>Margin setter kun hovedpris (price)</small>
                                                        )}
                                                    </div>
                                                    {wizardPreviewError && (
                                                        <div class='alert alert-danger mt-3 mb-0'>
                                                            <i class='fa-solid fa-triangle-exclamation me-2' />
                                                            {wizardPreviewError}
                                                        </div>
                                                    )}
                                                </>)}

                                                {/* Step 2: Filter */}
                                                {wizardStep === 2 && (<>
                                                    <div class='row g-3'>
                                                        <div class='col-md-4'>
                                                            <label class='form-label fw-bold'>Konto</label>
                                                            <select
                                                                class='form-select'
                                                                value={wizardAccount}
                                                                onChange={(e) => this.setState({ wizardAccount: e.target.value })}
                                                            >
                                                                <option value=''>-- Alle kontoer --</option>
                                                                {getAccountOptions(userStore.customer).map(([num, label]) => (
                                                                    <option value={num} key={num}>{label}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                        <div class='col-md-4'>
                                                            <label class='form-label fw-bold'>Kategori</label>
                                                            <select
                                                                class='form-select'
                                                                value={wizardCategory}
                                                                onChange={(e) => this.setState({ wizardCategory: e.target.value })}
                                                            >
                                                                <option value=''>-- Alle kategorier --</option>
                                                                <option value='produkt'>Produkt</option>
                                                                <option value='honorar'>Honorar</option>
                                                                <option value='medisin'>Medisin</option>
                                                                <option value='annet'>Annet</option>
                                                            </select>
                                                        </div>
                                                        <div class='col-md-4'>
                                                            <label class='form-label fw-bold'>Søk</label>
                                                            <input
                                                                class='form-control'
                                                                type='text'
                                                                value={wizardSearch}
                                                                placeholder='Søk i produktnavn...'
                                                                onInput={(e) => this.setState({ wizardSearch: e.target.value })}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div class='alert alert-info mt-3 mb-0'>
                                                        <i class='fa-solid fa-info-circle me-2' />
                                                        <strong>Valgt justering:</strong>{' '}
                                                        {wizardMode === 'percent' && <>{wizardPercent}% (prosent)</>}
                                                        {wizardMode === 'fixed' && <>{wizardAmount} kr (fast beløp)</>}
                                                        {wizardMode === 'margin' && <>{wizardMargin}% (margin)</>}
                                                        {wizardRounding > 0 && <>, avrundet til nærmeste {wizardRounding} kr {wizardRoundIncVat ? 'inkl. mva' : 'eks. mva'}</>}
                                                        {'. '}
                                                        Komponenter: {Object.keys(wizardComponents).filter(k => wizardMode === 'margin' ? k === 'price' : wizardComponents[k]).join(', ')}
                                                    </div>
                                                </>)}

                                                {/* Step 3: Simuler */}
                                                {wizardStep === 3 && (<>
                                                    {wizardIsLoading && (
                                                        <div class='text-center py-5'>
                                                            <i class='fa-solid fa-spinner fa-spin fa-2x text-primary' />
                                                            <div class='text-body-secondary mt-2'>Simulerer prisjustering...</div>
                                                        </div>
                                                    )}
                                                    {wizardPreviewError && !wizardIsLoading && (
                                                        <div class='alert alert-danger'>
                                                            <i class='fa-solid fa-triangle-exclamation me-2' />
                                                            {wizardPreviewError}
                                                            <button type='button' class='btn btn-sm btn-outline-danger ms-3' onClick={this.wizardRunPreview}>
                                                                <i class='fa-solid fa-rotate-right me-1' /> Prøv igjen
                                                            </button>
                                                        </div>
                                                    )}
                                                    {wizardPreview && !wizardIsLoading && (<>
                                                        <div class='d-flex justify-content-between align-items-center mb-3'>
                                                            <h6 class='mb-0'>
                                                                <i class='fa-solid fa-eye text-info me-2' />
                                                                {wizardPreviewMessage}
                                                            </h6>
                                                            <button type='button' class='btn btn-sm btn-outline-secondary rounded-pill' onClick={this.wizardRunPreview}>
                                                                <i class='fa-solid fa-rotate-right me-1' /> Simuler på nytt
                                                            </button>
                                                        </div>
                                                        <div class='table-responsive' style='max-height: 400px; overflow-y: auto;'>
                                                            <table class='table table-sm table-striped table-hover mb-0'>
                                                                <thead class='sticky-top bg-body-secondary'>
                                                                    <tr>
                                                                        <th style='width: 80px;'>ID</th>
                                                                        <th>Navn</th>
                                                                        {wizardMode === 'margin' && <th style='width: 100px;' class='text-end'>Innpris</th>}
                                                                        <th style='width: 120px;' class='text-end'><nobr>Gammel pris {(wizardRoundIncVat && wizardRounding > 0) ? '(inkl. mva)' : '(eks. mva)'}</nobr></th>
                                                                        <th style='width: 120px;' class='text-end'><nobr>Ny pris {(wizardRoundIncVat && wizardRounding > 0) ? '(inkl. mva)' : '(eks. mva)'}</nobr></th>
                                                                        <th style='width: 100px;' class='text-end'>Diff</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {wizardPreview.slice(0, 100).map(item => {
                                                                        if (item.skipped) {
                                                                            return (
                                                                                <tr key={item.id} class='text-body-secondary'>
                                                                                    <td>{item.id}</td>
                                                                                    <td>{item.name}</td>
                                                                                    {wizardMode === 'margin' && <td class='text-end'>-</td>}
                                                                                    <td class='text-end'><nobr>{util.format(item.oldPrice || 0, 2)}</nobr></td>
                                                                                    <td class='text-end'><nobr>{util.format(item.oldPrice || 0, 2)}</nobr></td>
                                                                                    <td class='text-end'>
                                                                                        <span class='badge bg-warning text-dark'>{item.skipReason}</span>
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        }
                                                                        const vatFactor = (wizardRoundIncVat && wizardRounding > 0) ? (1 + ((item.vat || 0) / 100)) : 1;
                                                                        const oldVal = ((item.oldPrice || 0) + (item.oldFee || 0) + (item.oldMedicine || 0) + (item.oldEquipment || 0) + (item.oldOther || 0)) * vatFactor;
                                                                        const newVal = ((item.newPrice || 0) + (item.newFee || 0) + (item.newMedicine || 0) + (item.newEquipment || 0) + (item.newOther || 0)) * vatFactor;
                                                                        const diff = newVal - oldVal;
                                                                        return (
                                                                            <tr key={item.id}>
                                                                                <td>{item.id}</td>
                                                                                <td>{item.name}</td>
                                                                                {wizardMode === 'margin' && <td class='text-end'><nobr>{util.format(item.inPrice || 0, 2)}</nobr></td>}
                                                                                <td class='text-end'><nobr>{util.format(oldVal, 2)}</nobr></td>
                                                                                <td class='text-end'><nobr>{util.format(newVal, 2)}</nobr></td>
                                                                                <td class={`text-end ${diff >= 0 ? 'text-success' : 'text-danger'}`}>
                                                                                    <nobr>{diff >= 0 ? '+' : ''}{util.format(diff, 2)}</nobr>
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                        {wizardPreview.length > 100 && (
                                                            <div class='text-body-secondary small mt-2'>
                                                                <i class='fa-solid fa-info-circle me-1' />
                                                                Viser de første 100 av {wizardPreview.length} produkter
                                                            </div>
                                                        )}
                                                    </>)}
                                                </>)}

                                                {/* Step 4: Bekreft */}
                                                {wizardStep === 4 && (<>
                                                    {!wizardApplyDone ? (<>
                                                        <div class='card bg-body-secondary mb-3'>
                                                            <div class='card-body'>
                                                                <h6 class='card-title'>Oppsummering</h6>
                                                                <ul class='list-unstyled mb-0'>
                                                                    <li>
                                                                        <strong>Modus:</strong>{' '}
                                                                        {wizardMode === 'percent' && <>Prosent ({wizardPercent}%)</>}
                                                                        {wizardMode === 'fixed' && <>Fast beløp ({wizardAmount} kr)</>}
                                                                        {wizardMode === 'margin' && <>Margin ({wizardMargin}%)</>}
                                                                    </li>
                                                                    {wizardRounding > 0 && <li><strong>Avrunding:</strong> Nærmeste {wizardRounding} kr ({wizardRoundIncVat ? 'inkl. mva' : 'eks. mva'})</li>}
                                                                    <li><strong>Komponenter:</strong> {Object.keys(wizardComponents).filter(k => wizardMode === 'margin' ? k === 'price' : wizardComponents[k]).join(', ')}</li>
                                                                    {(wizardAccount || wizardCategory || wizardSearch) && (
                                                                        <li>
                                                                            <strong>Filter:</strong>{' '}
                                                                            {wizardAccount && <>Konto: {wizardAccount} </>}
                                                                            {wizardCategory && <>Kategori: {wizardCategory} </>}
                                                                            {wizardSearch && <>Søk: "{wizardSearch}"</>}
                                                                        </li>
                                                                    )}
                                                                    {wizardPreview && (
                                                                        <li><strong>Antall produkter:</strong> {wizardPreview.filter(p => !p.skipped).length} (av {wizardPreview.length} totalt)</li>
                                                                    )}
                                                                </ul>
                                                            </div>
                                                        </div>
                                                        <div class='text-center'>
                                                            <button
                                                                type='button'
                                                                class='btn btn-lg btn-primary'
                                                                onClick={this.wizardApply}
                                                                disabled={wizardIsLoading}
                                                            >
                                                                {wizardIsLoading
                                                                    ? <><i class='fa-solid fa-spinner fa-spin me-2' /> Utfører...</>
                                                                    : <><i class='fa-solid fa-check me-2' /> Utfør prisjustering</>
                                                                }
                                                            </button>
                                                        </div>
                                                    </>) : (
                                                        <div class='text-center py-4'>
                                                            <i class='fa-solid fa-circle-check fa-3x text-success mb-3' />
                                                            <h5>Prisjustering fullført!</h5>
                                                            <p class='text-body-secondary'>Produktlisten oppdateres automatisk.</p>
                                                            <button type='button' class='btn btn-outline-secondary' onClick={this.togglePriceAdjustmentWizard}>
                                                                Lukk
                                                            </button>
                                                        </div>
                                                    )}
                                                </>)}
                                            </div>

                                            {/* Footer navigation */}
                                            {!wizardApplyDone && (
                                                <div class='card-footer d-flex justify-content-between'>
                                                    <div>
                                                        {wizardStep > 1 && (
                                                            <button type='button' class='btn btn-outline-secondary' onClick={() => this.wizardGoToStep(wizardStep - 1)}>
                                                                <i class='fa-solid fa-arrow-left me-1' /> Tilbake
                                                            </button>
                                                        )}
                                                    </div>
                                                    <div class='d-flex gap-2'>
                                                        <button type='button' class='btn btn-outline-secondary' onClick={this.togglePriceAdjustmentWizard}>
                                                            Avbryt
                                                        </button>
                                                        {wizardStep === 1 && (
                                                            <button
                                                                type='button'
                                                                class='btn btn-primary'
                                                                onClick={() => this.wizardGoToStep(2)}
                                                                disabled={
                                                                    (wizardMode === 'percent' && wizardPercent === '') ||
                                                                    (wizardMode === 'fixed' && wizardAmount === '') ||
                                                                    (wizardMode === 'margin' && wizardMargin === '')
                                                                }
                                                            >
                                                                Neste <i class='fa-solid fa-arrow-right ms-1' />
                                                            </button>
                                                        )}
                                                        {wizardStep === 2 && (
                                                            <button type='button' class='btn btn-primary' onClick={() => this.wizardGoToStep(3)}>
                                                                Neste <i class='fa-solid fa-arrow-right ms-1' />
                                                            </button>
                                                        )}
                                                        {wizardStep === 3 && wizardPreview && !wizardIsLoading && (
                                                            <button type='button' class='btn btn-primary' onClick={() => this.wizardGoToStep(4)}>
                                                                Gå til bekreftelse <i class='fa-solid fa-arrow-right ms-1' />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                </div>
                            </div>

                            <div class='mt-2'>
                                <Pagination total={total} limit={limit} current={currentPage} onPageChange={this.setPage} stores={this.props.stores} minimalistic={true} />
                            </div>

                            {tripletexMatchError && (
                                <div class='alert alert-danger alert-dismissible mx-3 mt-2' role='alert'>
                                    <i class='fa-solid fa-triangle-exclamation me-2' />
                                    {tripletexMatchError}
                                    <button type='button' class='btn-close' onClick={this.clearBulkTripletexMatches} aria-label='Close'></button>
                                </div>
                            )}

                            <div class={`d-flex flex-column mx-3 mt-2 px-3 py-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} font-style-narrow`}>
                                <div class={`table-responsive ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                    <table class='table table-sm table-striped mb-0'>
                                        <thead>
                                            <tr>
                                                {this.isColumnVisible('lineNumber') && <th class='d-none d-sm-table-cell w-5'>&nbsp;</th>}
                                                {this.isColumnVisible('velocity') && <th class='d-none d-sm-table-cell text-nowrap' style='width: 130px;'><Text id='product.movement'>Movement</Text></th>}
                                                {this.isColumnVisible('productNumber') && <th
                                                    class={`d-none d-sm-table-cell w-5 ${sort === 'productNumber' ? 'font-weight-bold' : ''}`}
                                                    onClick={this.setSort}
                                                    data-name='productNumber'
                                                >
                                                    <nobr>
                                                        <Text id='dyrejournal.productNumber'>productNumber</Text>
                                                        {sort === 'productNumber' && <><i class='fa-solid fa-arrow-up-a-z ms-2' /></>}
                                                        {sort === '-productNumber' && <><i class='fa-solid fa-arrow-down-a-z ms-2' /></>}
                                                    </nobr>
                                                </th>}
                                                {this.isColumnVisible('webshop') && <th
                                                    class={`d-none d-sm-table-cell w-5 ${sort === 'webshop' ? 'font-weight-bold' : ''}`}
                                                    style='writing-mode: vertical-rl; transform: rotate(180deg); white-space: nowrap; vertical-align: bottom;'
                                                    onClick={this.setSort}
                                                    data-name='webshop'
                                                >
                                                    <nobr>
                                                        <Text id='dyrejournal.webshop'>webshop</Text>
                                                        {sort === 'webshop' && <><i class='fa-solid fa-arrow-up-a-z ms-2' /></>}
                                                        {sort === '-webshop' && <><i class='fa-solid fa-arrow-down-a-z ms-2' /></>}
                                                    </nobr>
                                                </th>}
                                                {this.isColumnVisible('isMemberProduct') && <th
                                                    class={`d-none d-sm-table-cell w-5 ${sort === 'isMemberProduct' ? 'font-weight-bold' : ''}`}
                                                    onClick={this.setSort}
                                                    data-name='isMemberProduct'
                                                >
                                                    <nobr>
                                                        Member Product
                                                        {sort === 'isMemberProduct' && <><i class='fa-solid fa-arrow-up-a-z ms-2' /></>}
                                                        {sort === '-isMemberProduct' && <><i class='fa-solid fa-arrow-down-a-z ms-2' /></>}
                                                    </nobr>
                                                </th>}
                                                {this.isColumnVisible('idealStock') && <th
                                                    class={`d-none d-sm-table-cell w-5 ${sort === 'idealStock' ? 'font-weight-bold' : ''}`}
                                                    style='writing-mode: vertical-rl; transform: rotate(180deg); white-space: nowrap; vertical-align: bottom;'
                                                    onClick={this.setSort}
                                                    data-name='idealStock'
                                                >
                                                    <nobr>
                                                        <Text id='dyrejournal.idealStock'>idealStock</Text>
                                                        {sort === 'idealStock' && <><i class='fa-solid fa-arrow-up-a-z ms-2' /></>}
                                                        {sort === '-idealStock' && <><i class='fa-solid fa-arrow-down-a-z ms-2' /></>}
                                                    </nobr>
                                                </th>}
                                                {this.isColumnVisible('inStock') && <th
                                                    class={`d-none d-sm-table-cell w-5 ${sort === 'inStock' ? 'font-weight-bold' : ''}`}
                                                    style='writing-mode: vertical-rl; transform: rotate(180deg); white-space: nowrap; vertical-align: bottom;'
                                                    onClick={this.setSort}
                                                    data-name='inStock'
                                                >
                                                    <nobr>
                                                        <Text id='dyrejournal.inStock'>inStock</Text>
                                                        {sort === 'inStock' && <><i class='fa-solid fa-arrow-up-a-z ms-2' /></>}
                                                        {sort === '-inStock' && <><i class='fa-solid fa-arrow-down-a-z ms-2' /></>}
                                                    </nobr>
                                                </th>}
                                                {this.isColumnVisible('addStock') && <th
                                                    class={`w-5`}
                                                    style='writing-mode: vertical-rl; transform: rotate(180deg); white-space: nowrap; vertical-align: bottom;'
                                                >
                                                    <Text id='dyrejournal.product.add-stock'>Add Stock</Text>
                                                </th>}
                                                {this.isColumnVisible('name') && <th
                                                    class={`${sort === 'name' ? 'font-weight-bold' : ''}`}
                                                    style='min-width: 220px;'
                                                    onClick={this.setSort}
                                                    data-name='name'
                                                >
                                                    <nobr>
                                                        <Text id='dyrejournal.product.name'>Name</Text>
                                                        {sort === 'name' && <><i class='fa-solid fa-arrow-up-a-z ms-2' /></>}
                                                        {sort === '-name' && <><i class='fa-solid fa-arrow-down-a-z ms-2' /></>}
                                                    </nobr>
                                                </th>}
                                                {this.isColumnVisible('category') && <th
                                                    class={`${sort === 'category' ? 'font-weight-bold' : ''}`}
                                                    onClick={this.setSort}
                                                    data-name='category'
                                                >
                                                    <nobr>
                                                        <Text id='dyrejournal.product.category'>Category</Text>
                                                        {sort === 'category' && <><i class='fa-solid fa-arrow-up-a-z ms-2' /></>}
                                                        {sort === '-category' && <><i class='fa-solid fa-arrow-down-a-z ms-2' /></>}
                                                    </nobr>
                                                </th>}
                                                {this.isColumnVisible('journal') && <th class='text-center'>
                                                    <nobr>
                                                        <Text id='dyrejournal.product.journal-macro'>Journal Macro</Text>
                                                    </nobr>
                                                </th>}
                                                {this.isColumnVisible('drug') && <th class='text-center'>
                                                    <nobr>
                                                        <Text id='dyrejournal.product.drug-connected'>Drug Connected</Text>
                                                    </nobr>
                                                </th>}
                                                {this.isColumnVisible('account') && <th
                                                    class={`${sort === 'account' ? 'font-weight-bold' : ''}`}
                                                    onClick={this.setSort}
                                                    data-name='account'
                                                >
                                                    <nobr>
                                                        <Text id='dyrejournal.product.account'>Account</Text>
                                                        {sort === 'account' && <><i class='fa-solid fa-arrow-up-a-z ms-2' /></>}
                                                        {sort === '-account' && <><i class='fa-solid fa-arrow-down-a-z ms-2' /></>}
                                                    </nobr>
                                                </th>}
                                                {this.isColumnVisible('vat') && <th
                                                    class={`text-center w-5 ${sort === 'vat' ? 'font-weight-bold' : ''}`}
                                                    onClick={this.setSort}
                                                    data-name='vat'
                                                >
                                                    <Text id='dyrejournal.product.vat'>Vat</Text>
                                                    {sort === 'vat' && <><i class='fa-solid fa-arrow-up-a-z ms-2' /></>}
                                                    {sort === '-vat' && <><i class='fa-solid fa-arrow-down-a-z ms-2' /></>}
                                                </th>}
                                                {this.isColumnVisible('price') && <th
                                                    class={`text-center w-10 ${sort === 'price' ? 'font-weight-bold' : ''}`}
                                                    onClick={this.setSort}
                                                    data-name='price'
                                                >
                                                    <nobr>
                                                        <Text id='dyrejournal.product.price'>Price</Text>
                                                        {sort === 'price' && <><i class='fa-solid fa-arrow-up-a-z ms-2' /></>}
                                                        {sort === '-price' && <><i class='fa-solid fa-arrow-down-a-z ms-2' /></>}
                                                    </nobr>
                                                </th>}
                                                {this.isColumnVisible('medicine') && <th
                                                    class={`text-center w-10 ${sort === 'medicine' ? 'font-weight-bold' : ''}`}
                                                    onClick={this.setSort}
                                                    data-name='medicine'
                                                >
                                                    <nobr>
                                                        <Text id='dyrejournal.product.medicine'>medicine</Text>
                                                        {sort === 'medicine' && <><i class='fa-solid fa-arrow-up-a-z ms-2' /></>}
                                                        {sort === '-medicine' && <><i class='fa-solid fa-arrow-down-a-z ms-2' /></>}
                                                    </nobr>
                                                </th>}
                                                {this.isColumnVisible('fee') && <th
                                                    class={`text-center w-10 ${sort === 'fee' ? 'font-weight-bold' : ''}`}
                                                    onClick={this.setSort}
                                                    data-name='fee'
                                                >
                                                    <nobr>
                                                        <Text id='dyrejournal.product.fee'>fee</Text>
                                                        {sort === 'fee' && <><i class='fa-solid fa-arrow-up-a-z ms-2' /></>}
                                                        {sort === '-fee' && <><i class='fa-solid fa-arrow-down-a-z ms-2' /></>}
                                                    </nobr>
                                                </th>}
                                                {this.isColumnVisible('equipment') && <th
                                                    class={`text-center w-10 ${sort === 'equipment' ? 'font-weight-bold' : ''}`}
                                                    onClick={this.setSort}
                                                    data-name='equipment'
                                                >
                                                    <nobr>
                                                        <Text id='dyrejournal.product.equipment'>equipment</Text>
                                                        {sort === 'equipment' && <><i class='fa-solid fa-arrow-up-a-z ms-2' /></>}
                                                        {sort === '-equipment' && <><i class='fa-solid fa-arrow-down-a-z ms-2' /></>}
                                                    </nobr>
                                                </th>}
                                                {this.isColumnVisible('other') && <th
                                                    class={`text-center w-10 ${sort === 'other' ? 'font-weight-bold' : ''}`}
                                                    onClick={this.setSort}
                                                    data-name='other'
                                                >
                                                    <nobr>
                                                        <Text id='dyrejournal.product.other'>other</Text>
                                                        {sort === 'other' && <><i class='fa-solid fa-arrow-up-a-z ms-2' /></>}
                                                        {sort === '-other' && <><i class='fa-solid fa-arrow-down-a-z ms-2' /></>}
                                                    </nobr>
                                                </th>}
                                                {this.isColumnVisible('discountAmount') && <th
                                                    class={`w-10 ${sort === 'discountAmount' ? 'font-weight-bold' : ''}`}
                                                    style='writing-mode: vertical-rl; transform: rotate(180deg); white-space: nowrap; vertical-align: bottom;'
                                                    onClick={this.setSort}
                                                    data-name='discountAmount'
                                                >
                                                    <nobr>
                                                        <Text id='dyrejournal.product.discountAmount'>Discount</Text>
                                                        {sort === 'discountAmount' && <><i class='fa-solid fa-arrow-up-a-z ms-2' /></>}
                                                        {sort === '-discountAmount' && <><i class='fa-solid fa-arrow-down-a-z ms-2' /></>}
                                                    </nobr>
                                                </th>}
                                                {this.isColumnVisible('saleType') && <th
                                                    class={`w-10 ${sort === 'saleType' ? 'font-weight-bold' : ''}`}
                                                    style='writing-mode: vertical-rl; transform: rotate(180deg); white-space: nowrap; vertical-align: bottom;'
                                                    onClick={this.setSort}
                                                    data-name='saleType'
                                                >
                                                    <nobr>
                                                        <Text id='dyrejournal.product.saleType'>Sale Type</Text>
                                                        {sort === 'saleType' && <><i class='fa-solid fa-arrow-up-a-z ms-2' /></>}
                                                        {sort === '-saleType' && <><i class='fa-solid fa-arrow-down-a-z ms-2' /></>}
                                                    </nobr>
                                                </th>}
                                                {this.isColumnVisible('outPrice') && <th
                                                    class={`text-center w-10`}
                                                >
                                                    <Text id='dyrejournal.product.out-price'>Out Price</Text>
                                                </th>}
                                                {this.isColumnVisible('reminderDays') && <th
                                                    class={`text-center ${sort === 'reminderDays' || sort === '-reminderDays' ? 'font-weight-bold' : ''}`}
                                                    onClick={this.setSort}
                                                    data-name='reminderDays'
                                                    style='cursor: pointer;'
                                                >
                                                    <nobr>
                                                        <Text id='dyrejournal.product.reminder-days'>Reminder Days</Text>
                                                        {sort === 'reminderDays' && <><i class='fa-solid fa-arrow-up-1-9 ms-2' /></>}
                                                        {sort === '-reminderDays' && <><i class='fa-solid fa-arrow-down-9-1 ms-2' /></>}
                                                    </nobr>
                                                </th>}
                                                {this.isColumnVisible('reminderSmsText') && <th
                                                    class={`text-center`}
                                                >
                                                    <Text id='dyrejournal.product.reminder-sms-text'>Reminder SMS</Text>
                                                </th>}
                                                {showTripletex && this.isColumnVisible('tripletex') && <th style='writing-mode: vertical-rl; transform: rotate(180deg); white-space: nowrap; vertical-align: bottom;'>Tripletex</th>}
                                                {this.isColumnVisible('delete') && <th class='d-none d-sm-table-cell w-5'>&nbsp;</th>}
                                            </tr>
                                        </thead>
                                        <tbody
                                            onClick={this.selectCell}
                                            onKeyDown={this.keyNavigations}
                                            tabIndex='0'  // Ensure the tbody can be focused
                                            ref={c => this.tbodyRef = c}
                                        >
                                            {(products && products.length > 0) && products.map((product, row) => {
                                                let col = 0;
                                                // const hasGranularPrice = util.isDefined(product.medicine) || util.isDefined(product.fee) || util.isDefined(product.equipment) || util.isDefined(product.other)
                                                const hasGranularPrice = ['medicine', 'fee', 'equipment', 'other'].some(field => util.isDefined(product[field]) && parseFloat(product[field]) > 0);
                                                const lineNumber = (currentPage - 1) * limit + row + 1;

                                                return (<>
                                                    <tr onClick={skipChooseProduct ? undefined : this.onClickChooseProduct} data-id={product.id}>
                                                        {this.isColumnVisible('lineNumber') && <td data-col={0} data-row={row} class={`${selectedCell.col === 0 && selectedCell.row === row ? 'bg-secondary-subtle' : ''} d-none d-sm-table-cell`} onClick={this.onClickEditProduct} data-id={product.id}>
                                                            <nobr>
                                                                {lineNumber} &nbsp;
                                                                <i class='fa-solid fa-pen ml-2' />
                                                            </nobr>
                                                        </td>}
                                                        {this.isColumnVisible('velocity') && (() => {
                                                            const productWeeklyData = (tableWeeklyStats || [])
                                                                .filter(stat => stat.productId === product.id)
                                                                .sort((a, b) => a.year !== b.year ? a.year - b.year : a.week - b.week)
                                                                .map((stat, index) => ({
                                                                    x: index,
                                                                    y: stat.qty,
                                                                    label: `${stat.year}-W${stat.week}`,
                                                                }));
                                                            const totalQty = productWeeklyData.reduce((sum, d) => sum + d.y, 0);
                                                            const velocityPerWeek = totalQty / 26; // 6 months = ~26 weeks
                                                            return (
                                                                <td class='d-none d-sm-table-cell p-1 text-nowrap'>
                                                                    <div class='d-flex align-items-center flex-nowrap'>
                                                                        <MiniGraph
                                                                            stores={this.props.stores}
                                                                            dataLeft={[productWeeklyData]}
                                                                            width={80}
                                                                            height={24}
                                                                            showXTicks={0}
                                                                            showYTicksLeft={0}
                                                                            paddingLeft={2}
                                                                            paddingTop={2}
                                                                            smoothLeft={[true]}
                                                                            gradientFillLeft={['#4e79a7']}
                                                                            gradientOpacity={0.2}
                                                                            inputStyleLeft={'stroke: #4e79a7 !important; stroke-width: 1px;'}
                                                                        />
                                                                        <nobr><small class='text-muted ms-1'>{velocityPerWeek.toFixed(1)}/w</small></nobr>
                                                                    </div>
                                                                </td>
                                                            );
                                                        })()}
                                                        {this.isColumnVisible('productNumber') && <td data-col={1} data-row={row} class={`${selectedCell.col === 1 && selectedCell.row === row ? 'bg-info-subtle' : ''} d-none d-sm-table-cell`}>
                                                            {editingCell.col === 1 && editingCell.row === row ? <>
                                                                <ScrollIntoView block={'center'} />
                                                                <Localizer>
                                                                    <Input
                                                                        stores={this.props.stores}
                                                                        holdingStore={productStore}
                                                                        object={product}
                                                                        objectName={'product'}
                                                                        type={'text'}
                                                                        field={'productNumber'}
                                                                        value={product.productNumber}
                                                                        // title={field.title}
                                                                        placeholder={'productNumber'}
                                                                        onInput={this.adminUpdate}
                                                                        extraDataset={{
                                                                            'data-id': product.id,
                                                                        }}
                                                                        // styles={field.styles}
                                                                        isTable={true}
                                                                        setFocusToThis={true}
                                                                    />
                                                                </ Localizer>
                                                            </> : <>
                                                                {product.productNumber}
                                                            </>}
                                                        </td>}
                                                        {this.isColumnVisible('webshop') && <td data-col={2} data-row={row} class={`${selectedCell.col === 2 && selectedCell.row === row ? 'bg-info-subtle' : ''} d-none d-sm-table-cell text-center`}>
                                                            {editingCell.col === 2 && editingCell.row === row ? <>
                                                                <ScrollIntoView block={'center'} />
                                                                <Localizer>
                                                                    <Input
                                                                        stores={this.props.stores}
                                                                        holdingStore={productStore}
                                                                        object={product}
                                                                        objectName={'product'}
                                                                        type={'toggle'}
                                                                        field={'webshop'}
                                                                        value={product.webshop}
                                                                        // title={field.title}
                                                                        placeholder={'webshop'}
                                                                        onInput={this.adminUpdate}
                                                                        extraDataset={{
                                                                            'data-id': product.id,
                                                                        }}
                                                                        // styles={field.styles}
                                                                        isTable={true}
                                                                        setFocusToThis={true}
                                                                    />
                                                                </ Localizer>
                                                            </> : <>
                                                                {product?.webshop ? <><i class='fa-solid fa-check text-success' /></> : <><i class='fa-solid fa-times text-danger' /></> }
                                                            </>}
                                                        </td>}
                                                        {this.isColumnVisible('isMemberProduct') && <td class='d-none d-sm-table-cell text-center'>
                                                            <Localizer>
                                                                <Input
                                                                    stores={this.props.stores}
                                                                    holdingStore={productStore}
                                                                    object={product}
                                                                    objectName={'product'}
                                                                    type={'toggle'}
                                                                    field={'isMemberProduct'}
                                                                    value={product.isMemberProduct}
                                                                    placeholder={'isMemberProduct'}
                                                                    onInput={this.adminUpdate}
                                                                    extraDataset={{
                                                                        'data-id': product.id,
                                                                    }}
                                                                    isTable={true}
                                                                />
                                                            </Localizer>
                                                        </td>}
                                                        {this.isColumnVisible('idealStock') && <td data-col={3} data-row={row} class={`${selectedCell.col === 3 && selectedCell.row === row ? 'bg-info-subtle' : ''} d-none d-sm-table-cell text-center`}>
                                                            {editingCell.col === 3 && editingCell.row === row ? <>
                                                                <ScrollIntoView block={'center'} />
                                                                <Localizer>
                                                                    <Input
                                                                        stores={this.props.stores}
                                                                        holdingStore={productStore}
                                                                        object={product}
                                                                        objectName={'product'}
                                                                        type={'number'}
                                                                        field={'idealStock'}
                                                                        value={product.idealStock}
                                                                        // title={field.title}
                                                                        placeholder={'idealStock'}
                                                                        onInput={this.adminUpdate}
                                                                        extraDataset={{
                                                                            'data-id': product.id,
                                                                        }}
                                                                        // styles={field.styles}
                                                                        isTable={true}
                                                                        setFocusToThis={true}
                                                                    />
                                                                </ Localizer>
                                                            </> : <>
                                                                {product.idealStock}
                                                            </>}
                                                        </td>}
                                                        {this.isColumnVisible('inStock') && <td data-col={4} data-row={row} class={`${selectedCell.col === 4 && selectedCell.row === row ? 'bg-info-subtle' : ''} d-none d-sm-table-cell text-center`}>
                                                            {editingCell.col === 4 && editingCell.row === row ? <>
                                                                <ScrollIntoView block={'center'} />
                                                                <Localizer>
                                                                    <Input
                                                                        stores={this.props.stores}
                                                                        holdingStore={productStore}
                                                                        object={product}
                                                                        objectName={'product'}
                                                                        type={'number'}
                                                                        field={'inStock'}
                                                                        value={product.inStock}
                                                                        // title={field.title}
                                                                        placeholder={'inStock'}
                                                                        onInput={this.adminUpdate}
                                                                        extraDataset={{
                                                                            'data-id': product.id,
                                                                        }}
                                                                        // styles={field.styles}
                                                                        isTable={true}
                                                                        setFocusToThis={true}
                                                                    />
                                                                </ Localizer>
                                                            </> : <>
                                                                {product.inStock}
                                                            </>}
                                                        </td>}

                                                        {this.isColumnVisible('addStock') && <td data-col={5} data-row={row} data-runclick={true} onClick={this.onClickAddProductStock} class={`${selectedCell.col === 5 && selectedCell.row === row ? 'bg-secondary-subtle' : ''} d-none d-sm-table-cell text-center`}>
                                                            <i class='fa-regular fa-square-plus'></i>
                                                        </td>}

                                                        {this.isColumnVisible('name') && <td data-col={6} data-row={row} style='min-width: 220px; max-width: 400px;' class={`${selectedCell.col === 6 && selectedCell.row === row ? 'bg-info-subtle' : ''} d-none d-sm-table-cell`}>
                                                            {editingCell.col === 6 && editingCell.row === row ? <>
                                                                <ScrollIntoView block={'center'} />
                                                                <Localizer>
                                                                    <Input
                                                                        stores={this.props.stores}
                                                                        holdingStore={productStore}
                                                                        object={product}
                                                                        objectName={'product'}
                                                                        type={'text'}
                                                                        field={'name'}
                                                                        value={product.name}
                                                                        // title={field.title}
                                                                        placeholder={'name'}
                                                                        onInput={this.adminUpdate}
                                                                        extraDataset={{
                                                                            'data-id': product.id,
                                                                        }}
                                                                        // styles={field.styles}
                                                                        isTable={true}
                                                                        setFocusToThis={true}
                                                                    />
                                                                </ Localizer>
                                                            </> : <>
                                                                <div class='d-flex flex-row align-items-center flex-nowrap gap-1'>
                                                                <span class='text-truncate' style='min-width: 0;' title={product.name}>{product.name}</span>
                                                                {product.subProducts?.length > 0 && <Localizer>
                                                                    <small class='text-body-secondary text-nowrap flex-shrink-0' title={<Text id='dyrejournal.product.sub-products'>Sub Products</Text>}><i class='fa-duotone fa-layer-group' /> {product.subProducts.length}</small>
                                                                </Localizer>}
                                                                {product.journal > 0 && <Localizer>
                                                                    <small class='flex-shrink-0' title={<Text id='dyrejournal.product.journal-macro'>Journal Macro</Text>}><i class='fa-solid fa-wand-magic-sparkles text-success' /></small>
                                                                </Localizer>}
                                                                {product.drug > 0 && <Localizer>
                                                                    <small class='flex-shrink-0' title={<Text id='dyrejournal.product.drug-connected'>Drug Connected</Text>}><i class='fa-solid fa-pills text-primary' /></small>
                                                                </Localizer>}
                                                                {product.images?.length > 0 && <small class='text-body-secondary text-nowrap flex-shrink-0' style='cursor: pointer;' data-id={product.id} onMouseEnter={this.onImageBadgeMouseEnter} onMouseLeave={this.onImageBadgeMouseLeave}><i class='fa-duotone fa-images' /> {product.images.length}</small>}
                                                                <div class='d-flex flex-row align-items-center gap-1 flex-shrink-0'>
                                                                    {product.gtin && <>
                                                                        <Localizer>
                                                                            <button class='btn btn-link btn-sm py-0 px-1' type='button' onClick={this.onClickPrintBarcode} data-gtin={product.gtin} data-name={product.name} data-price={product.price} data-vat={product.vat} title={<Text id='dyrejournal.print-gtin-barcode'>Print GTIN barcode</Text>}>
                                                                                <small><i class='fa-duotone fa-barcode' /></small>
                                                                            </button>
                                                                        </Localizer>
                                                                    </>}
                                                                    <Localizer>
                                                                        <button class='btn btn-link btn-sm py-0 px-1' type='button' onClick={this.onClickPrintBarcode} data-productnumber={product.productNumber} data-name={product.name} data-price={product.price} data-vat={product.vat} title={<Text id='dyrejournal.print-product-number-barcode'>Print Product Number barcode</Text>}>
                                                                            <small><i class='fa-duotone fa-barcode' /></small>
                                                                        </button>
                                                                    </Localizer>
                                                                    <Localizer>
                                                                        <button class='btn btn-link btn-sm py-0 px-1' type='button' onClick={this.onClickShowPurchaseDetails} data-id={product.id} data-name={product.name} title={<Text id='dyrejournal.show-purchase-details'>Show purchase details</Text>}>
                                                                            <small><i class='fa-duotone fa-magnifying-glass' /></small>
                                                                        </button>
                                                                    </Localizer>
                                                                    <Localizer>
                                                                        <button class='btn btn-link btn-sm py-0 px-1' type='button' onClick={this.onClickPrintQrcode} data-id={product.id} data-uuidv4={product.uuidv4} data-name={product.name} data-productnumber={product.productNumber} data-price={product.price} data-vat={product.vat} title={<Text id='dyrejournal.print-qr'>Print QR</Text>}>
                                                                            <small><i class='fa-duotone fa-qrcode' /></small>
                                                                        </button>
                                                                    </Localizer>
                                                                    {product.uuidv4 && <Localizer>
                                                                        <button class='btn btn-link btn-sm py-0 px-1' type='button' onClick={this.onClickPrintCartQrcode} data-uuidv4={product.uuidv4} data-name={product.name} data-productnumber={product.productNumber} data-price={product.price} data-vat={product.vat} title={<Text id='dyrejournal.print-cart-qr'>Print add-to-cart QR</Text>}>
                                                                            <small><i class='fa-duotone fa-cart-plus' /></small>
                                                                        </button>
                                                                    </Localizer>}
                                                                    {this.viewTags(product)}
                                                                </div>
                                                                </div>
                                                            </>}
                                                        </td>}
                                                        {this.isColumnVisible('category') && <td data-col={7} data-row={row} class={`${selectedCell.col === 7 && selectedCell.row === row ? 'bg-secondary-subtle' : ''} d-none d-sm-table-cell`}>
                                                            {product.category}
                                                        </td>}
                                                        {this.isColumnVisible('journal') && <td data-row={row} class='d-none d-sm-table-cell text-center'>
                                                            {product.journal ? <i class='fa-solid fa-check text-success' /> : ''}
                                                        </td>}
                                                        {this.isColumnVisible('drug') && <td data-row={row} class='d-none d-sm-table-cell text-center'>
                                                            {product.drug ? <i class='fa-solid fa-check text-success' /> : ''}
                                                        </td>}
                                                        {this.isColumnVisible('account') && <td data-col={8} data-row={row} class={`${selectedCell.col === 8 && selectedCell.row === row ? 'bg-info-subtle' : ''} d-none d-sm-table-cell`}>
                                                            {editingCell.col === 8 && editingCell.row === row ? <>
                                                                <ScrollIntoView block={'center'} />
                                                                <Localizer>
                                                                    <Input
                                                                        stores={this.props.stores}
                                                                        holdingStore={productStore}
                                                                        object={product}
                                                                        objectName={'product'}
                                                                        type={'number'}
                                                                        field={'account'}
                                                                        value={product.account}
                                                                        placeholder={'account'}
                                                                        onInput={this.adminUpdate}
                                                                        extraDataset={{
                                                                            'data-id': product.id,
                                                                        }}
                                                                        isTable={true}
                                                                        setFocusToThis={true}
                                                                    />
                                                                </Localizer>
                                                            </> : <>
                                                                <span class='text-nowrap' title={getAccountName(product.account, userStore.customer)}>{product.account}</span>
                                                            </>}
                                                        </td>}
                                                        {this.isColumnVisible('vat') && <td data-col={9} data-row={row} class={`${selectedCell.col === 9 && selectedCell.row === row ? 'bg-info-subtle' : ''} d-none d-sm-table-cell text-end`}>
                                                            {editingCell.col === 9 && editingCell.row === row ? <>
                                                                <ScrollIntoView block={'center'} />
                                                                <Localizer>
                                                                    <Input
                                                                        stores={this.props.stores}
                                                                        holdingStore={productStore}
                                                                        object={product}
                                                                        objectName={'product'}
                                                                        type={'number'}
                                                                        field={'vat'}
                                                                        value={product.vat}
                                                                        // title={field.title}
                                                                        placeholder={'vat'}
                                                                        onInput={this.adminUpdate}
                                                                        extraDataset={{
                                                                            'data-id': product.id,
                                                                        }}
                                                                        // styles={field.styles}
                                                                        isTable={true}
                                                                        setFocusToThis={true}
                                                                    />
                                                                </ Localizer>
                                                            </> : <>
                                                                <nobr>
                                                                    {util.format(product.vat, 0, ',', ' ')}%
                                                                </nobr>
                                                            </>}
                                                        </td>}
                                                        {this.isColumnVisible('price') && <td data-col={10} data-row={row} class={`${selectedCell.col === 10 && selectedCell.row === row ? 'bg-info-subtle' : ''} text-end ${hasGranularPrice ? 'font-weight-lighter text-strikethrough text-muted' : ''}`}>
                                                            {editingCell.col === 10 && editingCell.row === row ? <>
                                                                <ScrollIntoView block={'center'} />
                                                                <Localizer>
                                                                    <Input
                                                                        stores={this.props.stores}
                                                                        holdingStore={productStore}
                                                                        object={product}
                                                                        objectName={'product'}
                                                                        type={'number'}
                                                                        field={'price'}
                                                                        value={product.price}
                                                                        // title={field.title}
                                                                        placeholder={'price'}
                                                                        onInput={this.adminUpdate}
                                                                        extraDataset={{
                                                                            'data-id': product.id,
                                                                        }}
                                                                        // styles={field.styles}
                                                                        isTable={true}
                                                                        setFocusToThis={true}
                                                                    />
                                                                </ Localizer>
                                                            </> : <>
                                                                <nobr>
                                                                    {formatPriceCell(product.price)}
                                                                </nobr>
                                                            </>}
                                                        </td>}
                                                        {this.isColumnVisible('medicine') && <td data-col={11} data-row={row} class={`${selectedCell.col === 11 && selectedCell.row === row ? 'bg-info-subtle' : ''} text-end`}>
                                                            {editingCell.col === 11 && editingCell.row === row ? <>
                                                                <ScrollIntoView block={'center'} />
                                                                <Localizer>
                                                                    <Input
                                                                        stores={this.props.stores}
                                                                        holdingStore={productStore}
                                                                        object={product}
                                                                        objectName={'product'}
                                                                        type={'number'}
                                                                        field={'medicine'}
                                                                        value={product.medicine}
                                                                        // title={field.title}
                                                                        placeholder={'medicine'}
                                                                        onInput={this.adminUpdate}
                                                                        extraDataset={{
                                                                            'data-id': product.id,
                                                                        }}
                                                                        // styles={field.styles}
                                                                        isTable={true}
                                                                        setFocusToThis={true}
                                                                    />
                                                                </ Localizer>
                                                            </> : <>
                                                                <nobr>
                                                                    {formatPriceCell(product.medicine)}
                                                                </nobr>
                                                            </>}
                                                        </td>}
                                                        {this.isColumnVisible('fee') && <td data-col={12} data-row={row} class={`${selectedCell.col === 12 && selectedCell.row === row ? 'bg-info-subtle' : ''} text-end`}>
                                                            {editingCell.col === 12 && editingCell.row === row ? <>
                                                                <ScrollIntoView block={'center'} />
                                                                <Localizer>
                                                                    <Input
                                                                        stores={this.props.stores}
                                                                        holdingStore={productStore}
                                                                        object={product}
                                                                        objectName={'product'}
                                                                        type={'number'}
                                                                        field={'fee'}
                                                                        value={product.fee}
                                                                        // title={field.title}
                                                                        placeholder={'fee'}
                                                                        onInput={this.adminUpdate}
                                                                        extraDataset={{
                                                                            'data-id': product.id,
                                                                        }}
                                                                        // styles={field.styles}
                                                                        isTable={true}
                                                                        setFocusToThis={true}
                                                                    />
                                                                </ Localizer>
                                                            </> : <>
                                                                <nobr>
                                                                    {formatPriceCell(product.fee)}
                                                                </nobr>
                                                            </>}
                                                        </td>}
                                                        {this.isColumnVisible('equipment') && <td data-col={13} data-row={row} class={`${selectedCell.col === 13 && selectedCell.row === row ? 'bg-info-subtle' : ''} text-end`}>
                                                            {editingCell.col === 13 && editingCell.row === row ? <>
                                                                <ScrollIntoView block={'center'} />
                                                                <Localizer>
                                                                    <Input
                                                                        stores={this.props.stores}
                                                                        holdingStore={productStore}
                                                                        object={product}
                                                                        objectName={'product'}
                                                                        type={'number'}
                                                                        field={'equipment'}
                                                                        value={product.equipment}
                                                                        // title={field.title}
                                                                        placeholder={'equipment'}
                                                                        onInput={this.adminUpdate}
                                                                        extraDataset={{
                                                                            'data-id': product.id,
                                                                        }}
                                                                        // styles={field.styles}
                                                                        isTable={true}
                                                                        setFocusToThis={true}
                                                                    />
                                                                </ Localizer>
                                                            </> : <>
                                                                <nobr>
                                                                    {formatPriceCell(product.equipment)}
                                                                </nobr>
                                                            </>}
                                                        </td>}
                                                        {this.isColumnVisible('other') && <td data-col={14} data-row={row} class={`${selectedCell.col === 14 && selectedCell.row === row ? 'bg-info-subtle' : ''} text-end`}>
                                                            {editingCell.col === 14 && editingCell.row === row ? <>
                                                                <ScrollIntoView block={'center'} />
                                                                <Localizer>
                                                                    <Input
                                                                        stores={this.props.stores}
                                                                        holdingStore={productStore}
                                                                        object={product}
                                                                        objectName={'product'}
                                                                        type={'number'}
                                                                        field={'other'}
                                                                        value={product.other}
                                                                        // title={field.title}
                                                                        placeholder={'other'}
                                                                        onInput={this.adminUpdate}
                                                                        extraDataset={{
                                                                            'data-id': product.id,
                                                                        }}
                                                                        // styles={field.styles}
                                                                        isTable={true}
                                                                        setFocusToThis={true}
                                                                    />
                                                                </ Localizer>
                                                            </> : <>
                                                                <nobr>
                                                                    {formatPriceCell(product.other)}
                                                                </nobr>
                                                            </>}
                                                        </td>}
                                                        {this.isColumnVisible('discountAmount') && <td data-col={15} data-row={row} class={`${selectedCell.col === 15 && selectedCell.row === row ? 'bg-info-subtle' : ''} text-end`}>
                                                            {editingCell.col === 15 && editingCell.row === row ? <>
                                                                <ScrollIntoView block={'center'} />
                                                                <Localizer>
                                                                    <Input
                                                                        stores={this.props.stores}
                                                                        holdingStore={productStore}
                                                                        object={product}
                                                                        objectName={'product'}
                                                                        type={'number'}
                                                                        field={'discountAmount'}
                                                                        value={product.discountAmount}
                                                                        placeholder={'discountAmount'}
                                                                        onInput={this.adminUpdate}
                                                                        extraDataset={{
                                                                            'data-id': product.id,
                                                                        }}
                                                                        isTable={true}
                                                                        setFocusToThis={true}
                                                                    />
                                                                </ Localizer>
                                                            </> : <>
                                                                <nobr>
                                                                    {formatPriceCell(product.discountAmount)}
                                                                </nobr>
                                                            </>}
                                                        </td>}
                                                        {this.isColumnVisible('saleType') && <td data-col={16} data-row={row} class={`${selectedCell.col === 16 && selectedCell.row === row ? 'bg-info-subtle' : ''}`}>
                                                            {editingCell.col === 16 && editingCell.row === row ? <>
                                                                <ScrollIntoView block={'center'} />
                                                                <Localizer>
                                                                    <Input
                                                                        stores={this.props.stores}
                                                                        holdingStore={productStore}
                                                                        object={product}
                                                                        objectName={'product'}
                                                                        type={'text'}
                                                                        field={'saleType'}
                                                                        value={product.saleType}
                                                                        placeholder={'e.g. Black Friday'}
                                                                        onInput={this.adminUpdate}
                                                                        extraDataset={{
                                                                            'data-id': product.id,
                                                                        }}
                                                                        isTable={true}
                                                                        setFocusToThis={true}
                                                                    />
                                                                </ Localizer>
                                                            </> : <>
                                                                <nobr>
                                                                    {product.saleType && <>
                                                                        <span class='badge bg-danger'>{product.saleType}</span>
                                                                    </>}
                                                                </nobr>
                                                            </>}
                                                        </td>}
                                                        {this.isColumnVisible('outPrice') && <td data-col={17} data-row={row} class={`${selectedCell.col === 17 && selectedCell.row === row ? 'bg-secondary-subtle' : ''} text-end`}>
                                                            <nobr>
                                                                {product.discountAmount > 0 ? <>
                                                                    <span class='text-muted' style='text-decoration: line-through; font-size: 0.85em;'>
                                                                        {formatPriceCell(product.price * (1 + (product.vat || 0) / 100))}
                                                                    </span>
                                                                    <span class='text-danger fw-bold ms-1'>
                                                                        {formatPriceCell(product.price * (1 + (product.vat || 0) / 100) - product.discountAmount)}
                                                                    </span>
                                                                </> : <>
                                                                    {saleStore.lineTotal(product, true, 1)}
                                                                </>}
                                                            </nobr>
                                                        </td>}
                                                        {this.isColumnVisible('reminderDays') && <td data-col={18} data-row={row} class={`${selectedCell.col === 18 && selectedCell.row === row ? 'bg-info-subtle' : ''} text-center`}>
                                                            {editingCell.col === 18 && editingCell.row === row ? <>
                                                                <ScrollIntoView block={'center'} />
                                                                <Localizer>
                                                                    <Input
                                                                        stores={this.props.stores}
                                                                        holdingStore={productStore}
                                                                        object={product}
                                                                        objectName={'product'}
                                                                        type={'number'}
                                                                        field={'reminderDays'}
                                                                        value={product.reminderDays}
                                                                        placeholder={'Days'}
                                                                        onInput={this.adminUpdate}
                                                                        extraDataset={{
                                                                            'data-id': product.id,
                                                                        }}
                                                                        isTable={true}
                                                                        setFocusToThis={true}
                                                                    />
                                                                </Localizer>
                                                            </> : <>
                                                                {product.reminderDays}
                                                            </>}
                                                        </td>}
                                                        {this.isColumnVisible('reminderSmsText') && <td data-col={19} data-row={row} class={`${selectedCell.col === 19 && selectedCell.row === row ? 'bg-info-subtle' : ''}`}>
                                                            {editingCell.col === 19 && editingCell.row === row ? <>
                                                                <ScrollIntoView block={'center'} />
                                                                <Localizer>
                                                                    <Input
                                                                        stores={this.props.stores}
                                                                        holdingStore={productStore}
                                                                        object={product}
                                                                        objectName={'product'}
                                                                        type={'text'}
                                                                        field={'reminderSmsText'}
                                                                        value={product.reminderSmsText}
                                                                        placeholder={'SMS text'}
                                                                        onInput={this.adminUpdate}
                                                                        extraDataset={{
                                                                            'data-id': product.id,
                                                                        }}
                                                                        isTable={true}
                                                                        setFocusToThis={true}
                                                                    />
                                                                </Localizer>
                                                            </> : <>
                                                                <small>{product.reminderSmsText}</small>
                                                            </>}
                                                        </td>}
                                                        {showTripletex && this.isColumnVisible('tripletex') && <td class='text-center'>
                                                            {(() => {
                                                                const isLinked = product.tripletexProductId > 0;
                                                                const matchData = bulkTripletexMatches[product.id];
                                                                const hasDiscrepancies = matchData?.discrepancies?.length > 0;
                                                                const isExpanded = expandedTripletexProductId === product.id;

                                                                if (isLinked) {
                                                                    return (
                                                                        <button
                                                                            class={`btn btn-sm btn-success rounded-pill ${isExpanded ? 'active' : ''}`}
                                                                            onClick={(e) => matchData ? this.onClickShowTripletexMatch(e, product.id) : (() => { e.stopPropagation(); this.onClickUnlinkTripletex(product.id); })()}
                                                                            title={matchData ? 'Klikk for å se detaljer' : `Koblet til Tripletex ID: ${product.tripletexProductId}. Klikk for å koble fra.`}
                                                                        >
                                                                            <i class='fa-solid fa-check' />
                                                                            {hasDiscrepancies && <span class='badge bg-warning ms-1'>!</span>}
                                                                        </button>
                                                                    );
                                                                }

                                                                if (matchData) {
                                                                    return (
                                                                        <button
                                                                            class={`btn btn-sm btn-${hasDiscrepancies ? 'warning' : 'outline-info'} rounded-pill ${isExpanded ? 'active' : ''}`}
                                                                            onClick={(e) => this.onClickShowTripletexMatch(e, product.id)}
                                                                            title='Klikk for å se detaljer og koble'
                                                                        >
                                                                            <i class='fa-solid fa-link' />
                                                                            {hasDiscrepancies && <span class='badge bg-danger ms-1'>!</span>}
                                                                        </button>
                                                                    );
                                                                }

                                                                return (
                                                                    <span class='text-muted' title='Ikke funnet i Tripletex'>
                                                                        <i class='fa-solid fa-minus' />
                                                                    </span>
                                                                );
                                                            })()}
                                                        </td>}
                                                        {this.isColumnVisible('delete') && <td data-col={20} data-row={row} class={`d-none d-sm-table-cell ${selectedCell.col === 20 && selectedCell.row === row ? 'bg-secondary-subtle' : ''}`} onClick={this.onClickDeleteProduct} data-id={product.id}>
                                                            <i class='fa-solid fa-trash text-danger' />
                                                        </td>}
                                                    </tr>
                                                    {/* Tripletex expandable match detail row */}
                                                    {showTripletex && expandedTripletexProductId === product.id && (() => {
                                                        const matchData = bulkTripletexMatches[product.id];
                                                        const isLinked = product.tripletexProductId > 0;
                                                        const visibleColumnCount = COLUMN_DEFINITIONS.filter(col => this.isColumnVisible(col.key)).length;

                                                        if (!matchData) {
                                                            return (
                                                                <tr>
                                                                    <td colspan={visibleColumnCount} class='bg-body-secondary'>
                                                                        <div class='p-3'>
                                                                            <div class='d-flex justify-content-between align-items-center'>
                                                                                <span class='text-muted'>Ingen Tripletex-data funnet. Kjør "Tripletex sjekk" først.</span>
                                                                                <button class='btn btn-sm btn-outline-secondary rounded-pill' onClick={this.onClickCloseTripletexMatch}>
                                                                                    <i class='fa-solid fa-times' />
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        }

                                                        const tp = matchData.tripletexProduct;
                                                        const hasDiscrepancies = matchData.discrepancies?.length > 0;

                                                        return (
                                                            <tr>
                                                                <td colspan={visibleColumnCount} class='bg-body-secondary'>
                                                                    <div class='p-3'>
                                                                        <div class='d-flex justify-content-between align-items-center mb-2'>
                                                                            <strong>
                                                                                <i class='fa-solid fa-exchange-alt me-2' />
                                                                                Tripletex treff
                                                                            </strong>
                                                                            <button class='btn btn-sm btn-outline-secondary rounded-pill' onClick={this.onClickCloseTripletexMatch}>
                                                                                <i class='fa-solid fa-times' />
                                                                            </button>
                                                                        </div>
                                                                        <div class='card'>
                                                                            <div class='card-body'>
                                                                                <div class='row'>
                                                                                    <div class='col-md-6'>
                                                                                        <h6 class='text-body-secondary'>Lokalt produkt</h6>
                                                                                        <div class='fw-bold'>{product.name}</div>
                                                                                        <div class='small text-body-secondary'>
                                                                                            <div>Produktnr: {product.productNumber || '-'}</div>
                                                                                            <div>ID: {product.id}</div>
                                                                                            <div>MVA: {product.vat}%</div>
                                                                                            <div>Konto: {product.account}</div>
                                                                                        </div>
                                                                                    </div>
                                                                                    <div class='col-md-6'>
                                                                                        <h6 class='text-body-secondary'>Tripletex produkt</h6>
                                                                                        <div class='fw-bold'>{tp.name}</div>
                                                                                        <div class='small text-body-secondary'>
                                                                                            <div>Produktnr: {tp.number || '-'}</div>
                                                                                            <div>ID: {tp.id}</div>
                                                                                            <div>MVA: {tp.vatType?.percentage || '-'}%</div>
                                                                                            <div>Konto: {tp.account?.number || '-'}</div>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                                {hasDiscrepancies && (
                                                                                    <div class='alert alert-warning mt-3 mb-2 py-2'>
                                                                                        <strong><i class='fa-solid fa-exclamation-triangle me-2' />Avvik funnet:</strong>
                                                                                        <ul class='mb-0 mt-1'>
                                                                                            {matchData.discrepancies.map((d, idx) => (
                                                                                                <li key={idx}>
                                                                                                    <strong>{d.fieldName}:</strong> Lokal = {d.local}, Tripletex = {d.tripletex}
                                                                                                </li>
                                                                                            ))}
                                                                                        </ul>
                                                                                    </div>
                                                                                )}
                                                                                <div class='d-flex gap-2 mt-3'>
                                                                                    {isLinked ? (
                                                                                        <button
                                                                                            class='btn btn-sm btn-outline-danger rounded-pill'
                                                                                            onClick={(e) => { e.stopPropagation(); this.onClickUnlinkTripletex(product.id); this.setState({ expandedTripletexProductId: null }); }}
                                                                                        >
                                                                                            <i class='fa-solid fa-unlink me-1' /> Koble fra
                                                                                        </button>
                                                                                    ) : (
                                                                                        <button
                                                                                            class='btn btn-sm btn-primary rounded-pill'
                                                                                            onClick={(e) => { e.stopPropagation(); this.onClickLinkTripletex(product.id, tp.id); this.setState({ expandedTripletexProductId: null }); }}
                                                                                        >
                                                                                            <i class='fa-solid fa-link me-1' /> Koble til Tripletex
                                                                                        </button>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })()}
                                                </>);
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                            </div>
                        </div>
                    </div>

                    <Pagination total={total} limit={limit} current={currentPage} onPageChange={this.setPage} stores={this.props.stores} />

                    {hoverImageProduct?.images?.[0] && <div class='position-fixed bg-body border rounded shadow-lg p-1' style={`left: ${hoverImagePos.left}px; top: ${hoverImagePos.top}px; z-index: 10001; pointer-events: none;`}>
                        <img
                            src={hoverImageProduct.images[0].s3MediumLink || hoverImageProduct.images[0].s3SmallLink || hoverImageProduct.images[0].s3LargeLink || hoverImageProduct.images[0].s3Link}
                            alt={hoverImageProduct.name}
                            style='max-width: 240px; max-height: 240px; object-fit: contain; display: block;'
                        />
                    </div>}

                </div>
            </div>
        );
    }
}

export default AllProducts;
